import CompanyOptionLabel from "../CompanyOptionLabel";
// src/components/PopupWindows/AddCompanyDialog.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  Autocomplete,
  Box,
  Stack,
  Typography,
  IconButton,
  TextField,
  Button,
  MenuItem,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
  TableCell,
} from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";

import CloseIcon from "@mui/icons-material/Close";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import { Link as LinkIcon } from "../icons/FigmaControlIcons";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import InlineLinkEntryCard from "./common/InlineLinkEntryCard";
import CompanySizeDropdownField from "../CompanySizeDropdownField";
import LocationSearchableField from "../LocationSearchableField";
import GenericDataTable from "../GenericDataTable";
import { deriveLinkAutofill, describeLinkAutofill } from "../../utils/linkAutofill";
import {
  buildCompanySocialPayloadFromLinks,
  isCompanySocialUrl,
} from "../../utils/companySocialLinks";
import {
  createTeamMemberIfMissing,
  fetchTeamMembers,
  normalizeTeamMemberName,
  upsertTeamMemberOption,
} from "../../utils/teamMembers";
import {
  COMPANY_STATUS_OPTIONS,
  normalizeCompanyStatusLabel,
} from "../../utils/companyStatuses";
import {
  dedupeAutocompleteOptions,
  normalizeAutocompleteText,
} from "../../utils/autocompleteOptions";
import { normalizeCompanySizeValue } from "../../utils/companySizeOptions";
import {
  normalizeContactImageUrl,
  normalizeContactSocialUrl,
} from "../../utils/contactChannels";
import { normalizeLocationValueForSave } from "../../utils/locationFilters";
import {
  pillButtonBaseSx,
  pillButtonInteractiveSx,
  popupNavigationPillSx,
  withPillButtonClassName,
} from "../../utils/pillButtonStyles";
import { buildEndpointUrls, shouldTryEndpointFallbackStatus } from "../../utils/apiUrls";
import { buildOpenCompanyNavPayload } from "../../utils/companyNavigation";
import { formatMissingRequiredFieldsMessage } from "../../utils/requiredFieldMessages";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const COMPANY_INDUSTRY_MAX = 100;
const teamMemberFilter = createFilterOptions();
const companyFilter = createFilterOptions();

const getStoredToken = () => {
  const direct =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("idToken") ||
    localStorage.getItem("id_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("idToken") ||
    sessionStorage.getItem("id_token");

  if (direct && String(direct).trim()) return String(direct).trim();

  const tryJsonKeys = ["user", "auth", "session", "profile"];
  for (const key of tryJsonKeys) {
    try {
      const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      const nestedToken =
        parsed?.token ||
        parsed?.accessToken ||
        parsed?.access_token ||
        parsed?.jwt ||
        parsed?.idToken ||
        parsed?.id_token;
      if (nestedToken && String(nestedToken).trim()) return String(nestedToken).trim();
    } catch {
      // ignore malformed session data
    }
  }

  return "";
};

/**
 * ✅ WHY your input “deselects itself” (focus/selection drops on every keypress)
 * You defined Card/Footer/ChoiceRow *inside* AddCompanyDialog.
 * In React, components defined inside render get a NEW identity every render,
 * so React unmounts/remounts their subtree -> inputs lose focus/selection.
 *
 * ✅ FIX:
 * Move those helper components OUTSIDE AddCompanyDialog so their identity is stable.
 */

const DEFAULT_FORM = {
  name: "",
  website: "",
  industry: "",
  size_range: "",
  location: "",
  status: "New",
  source: "Website",
  notes: "",
  owner_user_id: null,

  // UI-only (for the mock flow)
  company_link: "",
  team_member: "",
  career_site: "",
  email: "",
  phone: "",
};

const COMPANY_SOURCE_OPTIONS = [
  "Website",
  "LinkedIn",
  "Indeed",
  "Greenhouse",
  "Lever",
  "Workable",
  "Ashby",
  "Jobvite",
  "ZipRecruiter",
  "SmartRecruiters",
  "Monster",
  "Glassdoor",
  "Job board",
  "Referral",
  "Other",
];

const DIALOG_SCREEN_VALUES = new Set(["start", "link", "overview", "additional", "leadPreview"]);

function normalizeDialogScreen(value, fallback = "start") {
  const normalized = String(value || "").trim();
  return DIALOG_SCREEN_VALUES.has(normalized) ? normalized : fallback;
}

function getUserDisplayName(user = {}) {
  return (
    String(user?.name || "").trim() ||
    String([user?.first_name, user?.last_name].filter(Boolean).join(" ")).trim() ||
    String(user?.username || "").trim() ||
    String(user?.email || "").trim() ||
    ""
  );
}

function safeUrl(input) {
  const v = (input || "").trim();
  if (!v) return "";
  try {
    // eslint-disable-next-line no-new
    new URL(v.startsWith("http") ? v : `https://${v}`);
    return v.startsWith("http") ? v : `https://${v}`;
  } catch {
    return null;
  }
}

function normalizeCompanyWebsiteUrl(input) {
  const normalized = safeUrl(input);
  if (!normalized) return normalized;
  return isCompanySocialUrl(normalized) ? "" : normalized;
}

function buildSocialImportFromUrl(input) {
  const normalized = safeUrl(input);
  if (!normalized) {
    return {
      linkedin_url: "",
      twitter_url: "",
      facebook_url: "",
      instagram_url: "",
      youtube_url: "",
      tiktok_url: "",
    };
  }

  return buildCompanySocialPayloadFromLinks([normalized]);
}

function normalizeUniqueStrings(values = []) {
  return Array.from(
    new Set(
      (Array.isArray(values) ? values : [values])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );
}

function clampText(value, max) {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.length > max ? text.slice(0, max) : text;
}

function normalizeCompanyName(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function normalizeComparableCompanyName(value) {
  return normalizeCompanyName(value).toLowerCase();
}

function normalizeLookupValue(value = "") {
  return String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function getUrlHostname(value = "") {
  try {
    return new URL(String(value || "")).hostname.replace(/^www\./i, "").toLowerCase();
  } catch {
    return "";
  }
}

function buildCompanyOption(raw = {}) {
  const companyId = Number(raw?.company_id ?? raw?.companyId ?? raw?.id);
  const companyName = normalizeCompanyName(
    raw?.company_name ?? raw?.companyName ?? raw?.name ?? ""
  );
  if (!companyName) return null;

  return {
    company_id: Number.isFinite(companyId) && companyId > 0 ? companyId : null,
    company_name: companyName,
    company_logo: String(
      raw?.company_logo ??
        raw?.company_logo_url ??
        raw?.companyLogo ??
        raw?.logo ??
        ""
    ).trim(),
    website: String(
      raw?.company_website ?? raw?.companyWebsite ?? raw?.website ?? ""
    ).trim(),
    industry: String(raw?.company_industry ?? raw?.industry ?? "").trim(),
    size_range: String(
      raw?.company_size ?? raw?.size_range ?? raw?.sizeRange ?? ""
    ).trim(),
    career_site: String(
      raw?.company_career_website ??
        raw?.career_website ??
        raw?.career_site ??
        raw?.careerSite ??
        ""
    ).trim(),
    location: String(raw?.location ?? "").trim(),
    source: String(raw?.source ?? "").trim(),
    status: String(raw?.status ?? "").trim(),
  };
}

function upsertCompanyOption(options = [], nextOption = null) {
  if (!nextOption?.company_name) return Array.isArray(options) ? options : [];

  const normalizedName = normalizeLookupValue(nextOption.company_name);
  const nextList = Array.isArray(options) ? [...options] : [];
  const matchIndex = nextList.findIndex((option) => {
    if (
      nextOption.company_id != null &&
      option?.company_id != null &&
      String(option.company_id) === String(nextOption.company_id)
    ) {
      return true;
    }

    return normalizeLookupValue(option?.company_name || "") === normalizedName;
  });

  if (matchIndex >= 0) {
    nextList[matchIndex] = {
      ...nextList[matchIndex],
      ...nextOption,
    };
  } else {
    nextList.push(nextOption);
  }

  return nextList.sort((a, b) =>
    String(a?.company_name || "").localeCompare(String(b?.company_name || ""))
  );
}

function getCompanyOptionLabel(option) {
  if (typeof option === "string") return option;
  if (option?.inputValue) return option.inputValue;
  return String(option?.company_name || "");
}

function findMatchingCompanyOption(
  options = [],
  { companyName = "", website = "", allowWebsiteMatch = false } = {}
) {
  const normalizedName = normalizeLookupValue(companyName);
  const websiteHost = getUrlHostname(safeUrl(website) || website);

  if (allowWebsiteMatch && websiteHost) {
    const byWebsite = options.find(
      (option) => getUrlHostname(option?.website || "") === websiteHost
    );
    if (byWebsite) return byWebsite;
  }

  if (!normalizedName) return null;

  const exact = options.find(
    (option) => normalizeLookupValue(option?.company_name || "") === normalizedName
  );
  if (exact) return exact;
  return null;
}

const IMPORTED_COMPANY_NAME_STOP_WORDS = new Set([
  "and",
  "co",
  "company",
  "corp",
  "corporation",
  "group",
  "holding",
  "holdings",
  "inc",
  "incorporated",
  "limited",
  "llc",
  "ltd",
  "plc",
  "the",
]);
const IMPORTED_COMPANY_EXCLUDED_HOST_SUFFIXES = [
  "facebook.com",
  "instagram.com",
  "linkedin.com",
  "tiktok.com",
  "twitter.com",
  "x.com",
  "youtu.be",
  "youtube.com",
];
const IMPORTED_COMPANY_MEMBER_PAGE_PATH_HINT_REGEX =
  /\/(?:about|company|team|people|staff|leadership|leaders?|management|executive|founders?|officers?|our-team|our-people|meet-(?:the-)?team)(?:\/|$)/i;
const IMPORTED_COMPANY_TEAM_OR_LEADERSHIP_PAGE_PATH_HINT_REGEX =
  /\/(?:team|people|staff|leadership|leaders?|management|executive|founders?|officers?|our-team|our-people|meet-(?:the-)?team)(?:\/|$)/i;
const IMPORTED_COMPANY_NON_MEMBER_CONTENT_PATH_REGEX =
  /\/(?:blog|news|press|insights?|articles?|stories?|authors?)(?:\/|$)/i;
const IMPORTED_COMPANY_PERSONAL_PROFILE_PATH_HINT_REGEX = /^\/(?:in|pub)\//i;
const IMPORTED_COMPANY_CURRENT_EMPLOYMENT_SIGNAL_REGEX =
  /\b(present|current(?:ly)?|today|now)\b/i;
const IMPORTED_COMPANY_COMPLETED_DATE_RANGE_REGEX =
  /\b(?:(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)\s+)?\d{4}\s*-\s*(?:(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)\s+)?\d{4}\b/i;
const IMPORTED_COMPANY_NON_EMPLOYEE_SIGNAL_REGEX =
  /\b(former|previous|alumni|advisor|advisory|investor|board member|board observer|board of directors|consultant|contractor|customer|client|ex[-\s])/i;
const IMPORTED_COMPANY_ROLE_HINT_REGEX =
  /\b(ceo|cto|cfo|coo|chief|vp|vice president|president|founder|cofounder|director|head|lead|manager|recruit(?:er|ing)|talent|engineer|developer|officer|partner)\b/i;
const IMPORTED_COMPANY_WORKED_AT_SIGNAL_REGEX = /\bworked(?: at| for)?\b/i;

function normalizeImportedCompanyIdentityText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .map((part) => part.trim())
    .filter((part) => part && !IMPORTED_COMPANY_NAME_STOP_WORDS.has(part))
    .join(" ");
}

function normalizeImportedCompanySearchText(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function importedCompanyIdentityMatches(value, companyName) {
  const normalizedValue = normalizeImportedCompanyIdentityText(value);
  const normalizedCompany = normalizeImportedCompanyIdentityText(companyName);
  if (!normalizedValue || !normalizedCompany) return false;

  return (
    normalizedValue === normalizedCompany ||
    normalizedValue.includes(normalizedCompany) ||
    normalizedCompany.includes(normalizedValue)
  );
}

function getImportedCompanyHostKey(value) {
  const normalized = safeUrl(value);
  if (!normalized) return "";

  try {
    const host = new URL(normalized).hostname.replace(/^www\./i, "").toLowerCase();
    return IMPORTED_COMPANY_EXCLUDED_HOST_SUFFIXES.some(
      (suffix) => host === suffix || host.endsWith(`.${suffix}`)
    )
      ? ""
      : host;
  } catch {
    return "";
  }
}

function getImportedCompanyEmailHostKey(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw.includes("@")) return "";
  return raw.split("@").pop().replace(/^www\./i, "").trim();
}

function buildImportedCompanyHostKeys(...values) {
  return Array.from(new Set(values.map((value) => getImportedCompanyHostKey(value)).filter(Boolean)));
}

function importedCompanyHostMatches(host, companyHosts = []) {
  const normalizedHost = String(host || "").trim().toLowerCase();
  if (!normalizedHost) return false;

  return companyHosts.some(
    (candidateHost) =>
      normalizedHost === candidateHost ||
      normalizedHost.endsWith(`.${candidateHost}`) ||
      candidateHost.endsWith(`.${normalizedHost}`)
  );
}

function collectImportedCompanyMentionWindows(text, companyName) {
  const normalizedText = normalizeImportedCompanySearchText(text);
  const normalizedCompany = normalizeImportedCompanyIdentityText(companyName);
  if (!normalizedText || !normalizedCompany) return [];

  const windows = [];
  let searchIndex = 0;

  while (windows.length < 12) {
    const matchIndex = normalizedText.indexOf(normalizedCompany, searchIndex);
    if (matchIndex < 0) break;

    const windowStart = Math.max(0, matchIndex - 90);
    const windowEnd = Math.min(
      normalizedText.length,
      matchIndex + normalizedCompany.length + 90
    );
    windows.push(normalizedText.slice(windowStart, windowEnd));
    searchIndex = matchIndex + normalizedCompany.length;
  }

  return windows;
}

function scoreImportedCompanyMentionWindow(window, hasTitle = false) {
  if (!window) return 0;

  let score = 0;
  const hasCompletedDateRange =
    IMPORTED_COMPANY_COMPLETED_DATE_RANGE_REGEX.test(window) &&
    !IMPORTED_COMPANY_CURRENT_EMPLOYMENT_SIGNAL_REGEX.test(window);

  if (IMPORTED_COMPANY_CURRENT_EMPLOYMENT_SIGNAL_REGEX.test(window)) score += 3;
  if (IMPORTED_COMPANY_ROLE_HINT_REGEX.test(window)) score += 1;
  if (/\bat\b/i.test(window) && !hasCompletedDateRange) score += 1;
  if (hasTitle && !hasCompletedDateRange) score += 1;
  if (IMPORTED_COMPANY_NON_EMPLOYEE_SIGNAL_REGEX.test(window)) score -= 4;
  if (hasCompletedDateRange) score -= 3;
  if (IMPORTED_COMPANY_WORKED_AT_SIGNAL_REGEX.test(window)) score -= 2;

  return score;
}

function hasImportedCompanyCurrentRoleContext(text, companyName, hasTitle = false) {
  const windows = collectImportedCompanyMentionWindows(text, companyName);
  if (!windows.length) return false;
  return windows.some((window) => scoreImportedCompanyMentionWindow(window, hasTitle) > 0);
}

function hasImportedCompanyHistoricalOnlyContext(text, companyName, hasTitle = false) {
  const windows = collectImportedCompanyMentionWindows(text, companyName);
  if (!windows.length) return false;

  const scores = windows.map((window) =>
    scoreImportedCompanyMentionWindow(window, hasTitle)
  );
  return scores.some((score) => score < 0) && scores.every((score) => score < 0);
}

function sourceLooksLikeImportedCompanyPersonalProfile(sourceUrl) {
  const normalized = safeUrl(sourceUrl);
  if (!normalized) return false;

  try {
    const parsed = new URL(normalized);
    const host = parsed.hostname.replace(/^www\./i, "").toLowerCase();
    return (
      (host === "linkedin.com" || host.endsWith(".linkedin.com")) &&
      IMPORTED_COMPANY_PERSONAL_PROFILE_PATH_HINT_REGEX.test(parsed.pathname || "")
    );
  } catch {
    return false;
  }
}

function sourceIsOfficialCompanyTeamOrLeadershipPage(sourceUrl, companyHosts = []) {
  const normalized = safeUrl(sourceUrl);
  if (!normalized) return false;

  try {
    const parsed = new URL(normalized);
    const host = parsed.hostname.replace(/^www\./i, "").toLowerCase();
    const path = parsed.pathname || "";
    return (
      importedCompanyHostMatches(host, companyHosts) &&
      IMPORTED_COMPANY_TEAM_OR_LEADERSHIP_PAGE_PATH_HINT_REGEX.test(path) &&
      !IMPORTED_COMPANY_NON_MEMBER_CONTENT_PATH_REGEX.test(path)
    );
  } catch {
    return false;
  }
}

function importedContactBelongsToCompany(item = {}, context = {}) {
  if (!item || typeof item !== "object") return false;

  const companyName = String(
    context.company_name || context.name || context.company || ""
  ).trim();
  const companyHosts = buildImportedCompanyHostKeys(
    context.company_website || context.website || "",
    context.company_link || context.link || "",
    context.team_page_url || context.leadership_page_url || "",
    context.career_site || context.career_website || ""
  );

  if (!companyName && !companyHosts.length) return true;

  const title = String(
    item.title || item.role || item.position || item.contact_title || ""
  ).trim();
  const genericCompanyFields = [item.company, item.company_name]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const currentCompanyFields = [
    item.current_company,
    item.currentCompany,
    item.employer,
    item.organization,
    item.workplace,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
  const evidenceText = [
    item.evidence,
    item.match_reason,
    item.reason,
    item.summary,
    item.description,
    item.bio,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean)
    .join("\n");
  const sourceUrl = safeUrl(item.source_url || item.sourceUrl || item.source || "") || "";
  const sourceHost = getImportedCompanyHostKey(sourceUrl);
  const emailHost = getImportedCompanyEmailHostKey(item.email || item.contact_email || "");
  const contextualEvidenceText = [title, evidenceText].filter(Boolean).join("\n");
  const explicitCurrentCompanySignal = currentCompanyFields.some((value) =>
    importedCompanyIdentityMatches(value, companyName)
  );
  const explicitCompanySignal = genericCompanyFields.some((value) =>
    importedCompanyIdentityMatches(value, companyName)
  );
  const evidenceCompanySignal = importedCompanyIdentityMatches(evidenceText, companyName);
  const officialSourceSignal = importedCompanyHostMatches(sourceHost, companyHosts);
  const officialEmailSignal = importedCompanyHostMatches(emailHost, companyHosts);
  const officialTeamPageSignal =
    officialSourceSignal &&
    IMPORTED_COMPANY_MEMBER_PAGE_PATH_HINT_REGEX.test(sourceUrl);
  const officialTeamOrLeadershipPageSignal = sourceIsOfficialCompanyTeamOrLeadershipPage(
    sourceUrl,
    companyHosts
  );
  const personalProfileSignal = sourceLooksLikeImportedCompanyPersonalProfile(sourceUrl);
  const currentRoleContextSignal = hasImportedCompanyCurrentRoleContext(
    contextualEvidenceText,
    companyName,
    Boolean(title)
  );
  const historicalOnlyContextSignal = hasImportedCompanyHistoricalOnlyContext(
    contextualEvidenceText,
    companyName,
    Boolean(title)
  );
  const companySignal =
    explicitCurrentCompanySignal || explicitCompanySignal || evidenceCompanySignal;

  if (title && IMPORTED_COMPANY_NON_EMPLOYEE_SIGNAL_REGEX.test(title.toLowerCase())) {
    return false;
  }

  if (context.requireOrganizationPageSource || context.contact_source_policy === "team_leadership_only") {
    return officialTeamOrLeadershipPageSignal;
  }

  if (officialTeamPageSignal || officialEmailSignal) {
    return true;
  }

  if (explicitCurrentCompanySignal) {
    return true;
  }

  if (historicalOnlyContextSignal && !officialSourceSignal) {
    return false;
  }

  if (currentRoleContextSignal && companySignal) {
    return true;
  }

  if (
    officialSourceSignal &&
    companySignal &&
    Boolean(title || evidenceText)
  ) {
    return true;
  }

  if (
    !personalProfileSignal &&
    companySignal &&
    !historicalOnlyContextSignal &&
    Boolean(evidenceText || officialSourceSignal)
  ) {
    return true;
  }

  return false;
}

function doImportedCompanyAndJobConflict(companyImport = null, jobImport = null) {
  if (!companyImport || !jobImport) return false;
  if (!String(jobImport?.job_title || "").trim()) return false;

  const normalizedJobCompanyName = normalizeComparableCompanyName(jobImport?.company_name);
  const normalizedImportedCompanyName = normalizeComparableCompanyName(companyImport?.company_name);

  if (
    normalizedJobCompanyName &&
    normalizedImportedCompanyName &&
    normalizedJobCompanyName !== normalizedImportedCompanyName &&
    !normalizedJobCompanyName.includes(normalizedImportedCompanyName) &&
    !normalizedImportedCompanyName.includes(normalizedJobCompanyName)
  ) {
    return true;
  }

  const normalizedJobWebsite = normalizeCompanyWebsiteUrl(jobImport?.website || "");
  const normalizedImportedWebsite = normalizeCompanyWebsiteUrl(
    companyImport?.company_website || ""
  );

  return Boolean(
    normalizedJobWebsite &&
      normalizedImportedWebsite &&
      normalizedJobWebsite !== normalizedImportedWebsite
  );
}

function reconcileCompanyImportWithJobImport(companyImport = null, jobImport = null) {
  if (!companyImport) return null;
  return doImportedCompanyAndJobConflict(companyImport, jobImport) ? null : companyImport;
}

async function parseJsonSafe(response) {
  const rawText = await response.text();
  try {
    return rawText ? JSON.parse(rawText) : {};
  } catch {
    return {};
  }
}

async function requestJsonWithFallback({
  urls,
  method = "GET",
  token,
  body,
  isFormData = false,
}) {
  const candidates = Array.from(
    new Set(
      (Array.isArray(urls) ? urls : [urls])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );
  let lastError = null;

  for (const url of candidates) {
    try {
      const headers = {
        Accept: "application/json",
      };
      if (token) headers.Authorization = `Bearer ${token}`;
      if (!isFormData && body != null) headers["Content-Type"] = "application/json";

      const response = await fetch(url, {
        method,
        credentials: "include",
        headers,
        body: body == null ? undefined : isFormData ? body : JSON.stringify(body),
      });
      const data = await parseJsonSafe(response);

      if (shouldTryEndpointFallbackStatus(response.status)) {
        const fallbackMessage =
          response.status === 404
            ? `Endpoint not found (${response.status})`
            : `Endpoint temporarily unavailable (${response.status})`;
        lastError = new Error(data?.error || data?.message || fallbackMessage);
        lastError.status = response.status;
        continue;
      }

      return { response, data, url };
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error("Failed to reach the company service");
}

function normalizeCompanySaveErrorMessage(error) {
  const message = String(error?.message || "").trim();
  if (!message) return "Failed to create company";

  if (/failed to fetch/i.test(message)) {
    return "Could not reach the company service. Please check the API connection and try again.";
  }

  if (/(bad gateway|gateway time-?out|\b502\b|\b504\b)/i.test(message)) {
    return "The company service is temporarily unavailable. Please try again in a moment.";
  }

  if (/data too long for column '?industry'?/i.test(message)) {
    return "Industry is too long. It has been limited to fit the company record.";
  }

  return message;
}

function formatCareerLeadPreviewIssue(item) {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
    return String(item).trim();
  }
  if (Array.isArray(item)) {
    return item.map((entry) => formatCareerLeadPreviewIssue(entry)).filter(Boolean).join(" | ");
  }
  if (typeof item === "object") {
    const message = String(
      item.error ||
        item.message ||
        item.reason ||
        item.warning ||
        item.detail ||
        ""
    ).trim();
    const url = String(item.url || item.page || item.href || item.link || "").trim();

    if (message && url) return `${message} (${url})`;
    if (message) return message;
    if (url) return url;

    try {
      return JSON.stringify(item);
    } catch {
      return "";
    }
  }

  return String(item).trim();
}

function normalizeOtherLinks(items = []) {
  return (Array.isArray(items) ? items : [])
    .map((item) => {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        const url = safeUrl(item.url || item.href || item.link);
        const label = String(item.label || item.name || "").trim();
        if (!url) return null;
        return label ? { label, url } : { url };
      }

      const url = safeUrl(item);
      return url ? { url } : null;
    })
    .filter(Boolean);
}

function isLikelyJobPostingLink(value = "") {
  const normalized = safeUrl(value);
  if (!normalized) return false;

  try {
    const parsed = new URL(normalized);
    const host = parsed.hostname.replace(/^www\./i, "").toLowerCase();
    const pathname = parsed.pathname.toLowerCase();
    const search = parsed.search.toLowerCase();
    const jobBoardHosts = [
      "linkedin.com",
      "indeed.com",
      "greenhouse.io",
      "boards.greenhouse.io",
      "lever.co",
      "workable.com",
      "ashbyhq.com",
      "jobvite.com",
      "smartrecruiters.com",
      "ziprecruiter.com",
      "monster.com",
      "glassdoor.com",
    ];

    if (host.includes("linkedin.") && /\/jobs\/view\//i.test(pathname)) return true;
    if (host.includes("indeed.") && (pathname.includes("/viewjob") || parsed.searchParams.get("jk"))) {
      return true;
    }
    if (jobBoardHosts.some((candidate) => host === candidate || host.endsWith(`.${candidate}`))) {
      return true;
    }

    if (/[?&](gh_jid|gh_src|lever-source|ashby_jid|jobid|job_id|posting)=/i.test(search)) {
      return true;
    }

    const pathIndicators = [
      "/jobs/",
      "/job/",
      "/careers/",
      "/career/",
      "/positions/",
      "/position/",
      "/openings/",
      "/opening/",
      "/opportunities/",
      "/opportunity/",
      "/requisition/",
      "/requisitions/",
      "/vacancy/",
      "/vacancies/",
      "/roles/",
      "/apply/",
    ];

    if (pathIndicators.some((indicator) => pathname.includes(indicator))) {
      return ![
        "/jobs",
        "/jobs/",
        "/careers",
        "/careers/",
        "/career",
        "/career/",
      ].includes(pathname);
    }

    return false;
  } catch {
    return false;
  }
}

function normalizeParsedJobImport(parsed = {}) {
  const jobOpeningsRaw = parsed.job_openings ?? parsed.openings;
  const jobOpenings = Number(jobOpeningsRaw);

  return {
    job_title: String(parsed.job_title || parsed.title || "").trim(),
    company_name: String(parsed.company_name || parsed.company || "").trim(),
    source: String(parsed.source || "").trim(),
    post_link: safeUrl(parsed.post_link || parsed.job_url || ""),
    job_url: safeUrl(parsed.job_url || parsed.post_link || ""),
    employment: String(parsed.employment || parsed.employment_type || "").trim(),
    work_arrangement: String(parsed.work_arrangement || "").trim(),
    location: normalizeLocationValueForSave(parsed.location || ""),
    hiring_manager_name: String(
      parsed.hiring_manager_name || parsed.contact_name || ""
    ).trim(),
    job_openings: Number.isFinite(jobOpenings) && jobOpenings > 0 ? jobOpenings : null,
    description: String(parsed.description || "").trim(),
    responsibilities: String(parsed.responsibilities || "").trim(),
    qualifications: String(parsed.qualifications || "").trim(),
    education_requirements: String(
      parsed.education_requirements || parsed.education || ""
    ).trim(),
    experience_requirements: String(
      parsed.experience_requirements || parsed.experience || ""
    ).trim(),
    compensation: String(parsed.compensation || parsed.salary_range || "").trim(),
    skills: normalizeUniqueStrings(parsed.skills || []),
    soft_skills: normalizeUniqueStrings(parsed.soft_skills || []),
    interview_process: normalizeUniqueStrings(parsed.interview_process || []),
    company_industry: clampText(parsed.company_industry || parsed.industry || "", COMPANY_INDUSTRY_MAX),
    company_size: normalizeCompanySizeValue(parsed.company_size || parsed.size_range || ""),
    company_description: String(
      parsed.company_description || parsed.full_company_description || ""
    ).trim(),
    company_logo_url: safeUrl(parsed.company_logo_url || parsed.company_logo || ""),
    website: safeUrl(parsed.website || parsed.company_website || ""),
    career_website: safeUrl(
      parsed.career_website ||
        parsed.company_career_website ||
        parsed.post_link ||
        parsed.job_url ||
        ""
    ),
    other_links: normalizeOtherLinks(parsed.other_links || []),
  };
}

function normalizeParsedCompanyImport(parsed = {}) {
  const companyName = String(parsed.company_name || parsed.name || parsed.company || "").trim();
  const primaryWebsite = normalizeCompanyWebsiteUrl(
    parsed.company_website || parsed.website || parsed.company_url || parsed.companyWebsite || ""
  );
  const companyLink = safeUrl(parsed.company_link || parsed.company_page || parsed.link || "");
  const careerSite = normalizeCompanyWebsiteUrl(
    parsed.career_site ||
      parsed.career_website ||
      parsed.company_career_url ||
      parsed.company_career_website ||
      ""
  );
  const teamPageUrl =
    safeUrl(
      parsed.team_page_url ||
        parsed.teamPageUrl ||
        parsed.leadership_page_url ||
        parsed.leadershipPageUrl ||
        parsed.organization_page_url ||
        parsed.organizationPageUrl ||
        ""
    ) || "";
  const contactFilterContext = {
    company_name: companyName,
    company_website: primaryWebsite,
    company_link: companyLink,
    career_site: careerSite,
    team_page_url: teamPageUrl,
    linkedin_url: parsed.linkedin_url || parsed.linkedin || "",
    requireOrganizationPageSource: true,
  };
  const normalizeImportedContacts = (value = []) =>
    (Array.isArray(value) ? value : [])
      .filter((item) => importedContactBelongsToCompany(item, contactFilterContext))
      .map((item) => {
        if (!item || typeof item !== "object") return null;

        const linkedinUrl = normalizeContactSocialUrl(
          "linkedin",
          item.linkedin_url || item.linkedinUrl || item.linkedin || ""
        );
        const profileImage = normalizeContactImageUrl(
          item.profile_image ||
            item.profileImage ||
            item.avatar_url ||
            item.avatar ||
            item.photo_url ||
            item.image ||
            ""
        );

        const name = String(
          item.name || item.full_name || item.contact_name || ""
        ).trim();
        if (!name) return null;

        return {
          name,
          title: String(
            item.title || item.role || item.position || item.contact_title || ""
          ).trim(),
          company: String(
            item.company ||
              item.company_name ||
              item.current_company ||
              item.currentCompany ||
              item.employer ||
              item.organization ||
              item.workplace ||
              ""
          ).trim(),
          company_name: String(
            item.company_name ||
              item.company ||
              item.current_company ||
              item.currentCompany ||
              item.employer ||
              item.organization ||
              item.workplace ||
              ""
          ).trim(),
          current_company: String(
            item.current_company ||
              item.currentCompany ||
              item.company ||
              item.company_name ||
              item.employer ||
              item.organization ||
              item.workplace ||
              ""
          ).trim(),
          currentCompany: String(
            item.currentCompany ||
              item.current_company ||
              item.company ||
              item.company_name ||
              item.employer ||
              item.organization ||
              item.workplace ||
              ""
          ).trim(),
          employer: String(
            item.employer ||
              item.organization ||
              item.workplace ||
              item.current_company ||
              item.currentCompany ||
              ""
          ).trim(),
          organization: String(
            item.organization ||
              item.employer ||
              item.workplace ||
              item.current_company ||
              item.currentCompany ||
              ""
          ).trim(),
          workplace: String(
            item.workplace ||
              item.organization ||
              item.employer ||
              item.current_company ||
              item.currentCompany ||
              ""
          ).trim(),
          email: String(item.email || item.contact_email || "").trim(),
          phone: String(item.phone || item.contact_phone || "").trim(),
          linkedin_url: linkedinUrl || "",
          twitter_url:
            normalizeContactSocialUrl(
              "twitter",
              item.twitter_url || item.twitterUrl || item.twitter || item.x_url || item.xUrl || ""
            ) || "",
          facebook_url:
            normalizeContactSocialUrl("facebook", item.facebook_url || item.facebookUrl || item.facebook || "") || "",
          instagram_url:
            normalizeContactSocialUrl(
              "instagram",
              item.instagram_url || item.instagramUrl || item.instagram || ""
            ) || "",
          youtube_url:
            normalizeContactSocialUrl("youtube", item.youtube_url || item.youtubeUrl || item.youtube || "") || "",
          tiktok_url:
            normalizeContactSocialUrl("tiktok", item.tiktok_url || item.tiktokUrl || item.tiktok || "") || "",
          profile_image: profileImage || "",
          source_url: safeUrl(item.source_url || item.sourceUrl || "") || "",
          evidence: String(item.evidence || item.match_reason || "").trim(),
        };
      })
      .filter(Boolean);
  const socialFromKnownLinks = buildCompanySocialPayloadFromLinks(
    [
      parsed.linkedin_url || parsed.linkedin || "",
      parsed.twitter_url || parsed.twitter || parsed.x_url || "",
      parsed.facebook_url || parsed.facebook || "",
      parsed.instagram_url || parsed.instagram || "",
      parsed.youtube_url || parsed.youtube || "",
      parsed.tiktok_url || parsed.tiktok || "",
      companyLink || "",
      primaryWebsite || "",
      careerSite || "",
    ].filter(Boolean)
  );

  return {
    company_name: companyName,
    company_website: primaryWebsite,
    company_logo_url: safeUrl(parsed.company_logo_url || parsed.company_logo || ""),
    company_link: companyLink,
    career_site: careerSite,
    team_page_url: teamPageUrl,
    location: normalizeLocationValueForSave(parsed.location || ""),
    industry_sector: clampText(parsed.industry_sector || parsed.industry || "", COMPANY_INDUSTRY_MAX),
    founded_year: String(parsed.founded_year || parsed.founded || "").trim(),
    company_size: normalizeCompanySizeValue(parsed.company_size || parsed.size_range || ""),
    ownership_type: String(parsed.ownership_type || "").trim(),
    parent_company: String(parsed.parent_company || parsed.parent_company_subsidiaries || "").trim(),
    work_policies: String(parsed.work_policies || "").trim(),
    recruitment_tools: normalizeUniqueStrings(parsed.recruitment_tools || []),
    job_boards: normalizeUniqueStrings(parsed.job_boards || []),
    job_board_rating: String(parsed.job_board_rating || parsed.glassdoor_rating || "").trim(),
    company_email: String(parsed.company_email || parsed.email || "").trim(),
    company_phone: String(parsed.company_phone || parsed.phone || "").trim(),
    linkedin_url: safeUrl(parsed.linkedin_url || parsed.linkedin || socialFromKnownLinks.linkedin_url || ""),
    twitter_url: safeUrl(parsed.twitter_url || parsed.twitter || parsed.x_url || socialFromKnownLinks.twitter_url || ""),
    facebook_url: safeUrl(parsed.facebook_url || parsed.facebook || socialFromKnownLinks.facebook_url || ""),
    instagram_url: safeUrl(parsed.instagram_url || parsed.instagram || socialFromKnownLinks.instagram_url || ""),
    youtube_url: safeUrl(parsed.youtube_url || parsed.youtube || socialFromKnownLinks.youtube_url || ""),
    tiktok_url: safeUrl(parsed.tiktok_url || parsed.tiktok || socialFromKnownLinks.tiktok_url || ""),
    elevator_pitch: String(parsed.elevator_pitch || "").trim(),
    full_company_description: String(
      parsed.full_company_description || parsed.company_description || ""
    ).trim(),
    mission_and_vision: String(parsed.mission_and_vision || "").trim(),
    key_products_services: String(parsed.key_products_services || "").trim(),
    unique_selling_points: String(parsed.unique_selling_points || "").trim(),
    employee_perks: String(parsed.employee_perks || "").trim(),
    achievements_awards: String(parsed.achievements_awards || "").trim(),
    contacts: normalizeImportedContacts(
      parsed.contacts || parsed.team_contacts || parsed.team_members || []
    ),
  };
}

function buildMergedCompanyFormState(currentForm = {}, companyImport = null, jobImport = null, linkAutofill = {}) {
  const shouldPreferJobCompanyIdentity = Boolean(String(jobImport?.job_title || "").trim());
  const nextCompanyName =
    (shouldPreferJobCompanyIdentity ? jobImport?.company_name : companyImport?.company_name) ||
    (shouldPreferJobCompanyIdentity ? companyImport?.company_name : jobImport?.company_name) ||
    currentForm.name ||
    linkAutofill.companyName ||
    "";
  const nextWebsite =
    normalizeCompanyWebsiteUrl(
      shouldPreferJobCompanyIdentity ? jobImport?.website : companyImport?.company_website
    ) ||
    normalizeCompanyWebsiteUrl(
      shouldPreferJobCompanyIdentity ? companyImport?.company_website : jobImport?.website
    ) ||
    normalizeCompanyWebsiteUrl(currentForm.website) ||
    normalizeCompanyWebsiteUrl(linkAutofill.website) ||
    "";
  const nextCareerSite =
    normalizeCompanyWebsiteUrl(companyImport?.career_site) ||
    normalizeCompanyWebsiteUrl(jobImport?.career_website) ||
    normalizeCompanyWebsiteUrl(currentForm.career_site) ||
    "";
  const currentStatus = normalizeCompanyStatusLabel(currentForm.status, {
    fallback: DEFAULT_FORM.status,
    preserveUnknown: true,
  });

  return {
    ...currentForm,
    name: nextCompanyName || currentForm.name,
    website: nextWebsite || normalizeCompanyWebsiteUrl(currentForm.website) || "",
    industry: clampText(
      companyImport?.industry_sector ||
        jobImport?.company_industry ||
        currentForm.industry,
      COMPANY_INDUSTRY_MAX
    ),
    size_range: normalizeCompanySizeValue(
      companyImport?.company_size ||
        jobImport?.company_size ||
        currentForm.size_range
    ),
    location:
      companyImport?.location ||
      jobImport?.location ||
      currentForm.location,
    source:
      jobImport?.source ||
      linkAutofill.source ||
      currentForm.source,
    career_site: nextCareerSite || normalizeCompanyWebsiteUrl(currentForm.career_site) || "",
    email: companyImport?.company_email || currentForm.email,
    phone: companyImport?.company_phone || currentForm.phone,
    status:
      jobImport?.job_title && currentStatus === "New"
        ? "New"
        : currentStatus,
  };
}

function buildExistingCompanyFormState(currentForm = {}, companyRecord = {}) {
  const nextCompanyImport = normalizeParsedCompanyImport(companyRecord);
  const nextWebsite =
    normalizeCompanyWebsiteUrl(
      companyRecord?.website || companyRecord?.company_website || nextCompanyImport?.company_website
    ) || "";
  const nextCareerSite =
    normalizeCompanyWebsiteUrl(
      companyRecord?.career_site ||
        companyRecord?.career_website ||
        nextCompanyImport?.career_site
    ) || "";
  const nextCompanyLink =
    safeUrl(
      companyRecord?.company_link ||
        companyRecord?.linkedin_url ||
        nextCompanyImport?.company_link ||
        ""
    ) || "";

  return {
    ...currentForm,
    name: normalizeCompanyName(
      companyRecord?.name || companyRecord?.company_name || currentForm?.name || ""
    ),
    website: nextWebsite,
    industry: clampText(
      companyRecord?.industry ||
        companyRecord?.company_industry ||
        companyRecord?.industry_sector ||
        nextCompanyImport?.industry_sector ||
        currentForm?.industry,
      COMPANY_INDUSTRY_MAX
    ),
    size_range: normalizeCompanySizeValue(
      companyRecord?.size_range ||
        companyRecord?.company_size ||
        nextCompanyImport?.company_size ||
        currentForm?.size_range
    ),
    location:
      normalizeLocationValueForSave(
        companyRecord?.location || nextCompanyImport?.location || currentForm?.location || ""
      ) || "",
    status: normalizeCompanyStatusLabel(companyRecord?.status || currentForm?.status, {
      fallback: DEFAULT_FORM.status,
      preserveUnknown: true,
    }),
    source:
      String(companyRecord?.source || currentForm?.source || DEFAULT_FORM.source).trim() ||
      DEFAULT_FORM.source,
    notes: String(
      companyRecord?.notes ||
        companyRecord?.full_company_description ||
        companyRecord?.company_description ||
        currentForm?.notes ||
        ""
    ).trim(),
    company_link: nextCompanyLink,
    career_site: nextCareerSite,
    email: String(
      companyRecord?.company_email ||
        companyRecord?.contact_email ||
        companyRecord?.email ||
        nextCompanyImport?.company_email ||
        currentForm?.email ||
        ""
    ).trim(),
    phone: String(
      companyRecord?.company_phone ||
        companyRecord?.contact_phone ||
        companyRecord?.phone ||
        nextCompanyImport?.company_phone ||
        currentForm?.phone ||
        ""
    ).trim(),
    team_member: String(
      companyRecord?.team_member ||
        companyRecord?.owner_name ||
        companyRecord?.owner ||
        currentForm?.team_member ||
        ""
    ).trim(),
    owner_user_id:
      companyRecord?.owner_user_id !== undefined
        ? companyRecord.owner_user_id
        : currentForm?.owner_user_id ?? null,
  };
}

function buildCompanyPayload(form = {}, jobImport = null, companyImport = null) {
  const shouldTreatAsJobImport = Boolean(String(jobImport?.job_title || "").trim());
  const normalizedStatus = normalizeCompanyStatusLabel(form.status, {
    fallback: DEFAULT_FORM.status,
    preserveUnknown: true,
  });
  const source = String(jobImport?.source || form.source || "").trim() || null;
  const website =
    normalizeCompanyWebsiteUrl(
      shouldTreatAsJobImport
        ? jobImport?.website || companyImport?.company_website || form.website
        : companyImport?.company_website || jobImport?.website || form.website
    ) || null;
  const careerSite =
    normalizeCompanyWebsiteUrl(companyImport?.career_site || jobImport?.career_website || form.career_site) || null;
  const rawCompanyLinkCandidate =
    companyImport?.company_link ||
    companyImport?.linkedin_url ||
    (!shouldTreatAsJobImport || !isLikelyJobPostingLink(form.company_link)
      ? form.company_link
      : "");
  const companyLink = safeUrl(rawCompanyLinkCandidate) || null;
  const industry =
    clampText(
      companyImport?.industry_sector || jobImport?.company_industry || form.industry || "",
      COMPANY_INDUSTRY_MAX
    ) || null;
  const sizeRange =
    normalizeCompanySizeValue(
      companyImport?.company_size || jobImport?.company_size || form.size_range || ""
    ) || null;
  const location =
    normalizeLocationValueForSave(companyImport?.location || jobImport?.location || form.location || "") || null;
  const fullCompanyDescription =
    String(
      companyImport?.full_company_description || jobImport?.company_description || ""
    ).trim() || null;
  const logo =
    safeUrl(companyImport?.company_logo_url || jobImport?.company_logo_url) || null;
  const companyEmail =
    String(companyImport?.company_email || form.email || "").trim() || null;
  const companyPhone =
    String(companyImport?.company_phone || form.phone || "").trim() || null;
  const inferredSocialLinks = buildCompanySocialPayloadFromLinks(
    [companyImport?.company_link, companyImport?.linkedin_url, rawCompanyLinkCandidate].filter(
      Boolean
    )
  );
  const linkedinUrl = safeUrl(companyImport?.linkedin_url || inferredSocialLinks.linkedin_url || "") || null;
  const twitterUrl = safeUrl(companyImport?.twitter_url || inferredSocialLinks.twitter_url || "") || null;
  const facebookUrl = safeUrl(companyImport?.facebook_url || inferredSocialLinks.facebook_url || "") || null;
  const instagramUrl = safeUrl(companyImport?.instagram_url || inferredSocialLinks.instagram_url || "") || null;
  const youtubeUrl = safeUrl(companyImport?.youtube_url || inferredSocialLinks.youtube_url || "") || null;
  const tiktokUrl = safeUrl(companyImport?.tiktok_url || inferredSocialLinks.tiktok_url || "") || null;
  const recruitmentTools = normalizeUniqueStrings(companyImport?.recruitment_tools || []);
  const jobBoards = normalizeUniqueStrings(
    companyImport?.job_boards || (source ? [source] : [])
  );
  const importedContacts = Array.isArray(companyImport?.contacts)
    ? companyImport.contacts
        .map((contact) => ({
          name: String(contact?.name || "").trim(),
          title: String(contact?.title || contact?.role || "").trim(),
          email: String(contact?.email || "").trim(),
          phone: String(contact?.phone || "").trim(),
          linkedin_url:
            normalizeContactSocialUrl("linkedin", contact?.linkedin_url || "") || null,
          twitter_url:
            normalizeContactSocialUrl(
              "twitter",
              contact?.twitter_url || contact?.x_url || contact?.twitter || ""
            ) || null,
          facebook_url:
            normalizeContactSocialUrl("facebook", contact?.facebook_url || contact?.facebook || "") || null,
          instagram_url:
            normalizeContactSocialUrl(
              "instagram",
              contact?.instagram_url || contact?.instagram || ""
            ) || null,
          youtube_url:
            normalizeContactSocialUrl("youtube", contact?.youtube_url || contact?.youtube || "") || null,
          tiktok_url:
            normalizeContactSocialUrl("tiktok", contact?.tiktok_url || contact?.tiktok || "") || null,
          company:
            String(
              contact?.company ||
                contact?.company_name ||
                contact?.current_company ||
                contact?.currentCompany ||
                contact?.employer ||
                contact?.organization ||
                contact?.workplace ||
                ""
            ).trim() || null,
          company_name:
            String(
              contact?.company_name ||
                contact?.company ||
                contact?.current_company ||
                contact?.currentCompany ||
                contact?.employer ||
                contact?.organization ||
                contact?.workplace ||
                ""
            ).trim() || null,
          current_company:
            String(
              contact?.current_company ||
                contact?.currentCompany ||
                contact?.company ||
                contact?.company_name ||
                contact?.employer ||
                contact?.organization ||
                contact?.workplace ||
                ""
            ).trim() || null,
          currentCompany:
            String(
              contact?.currentCompany ||
                contact?.current_company ||
                contact?.company ||
                contact?.company_name ||
                contact?.employer ||
                contact?.organization ||
                contact?.workplace ||
                ""
            ).trim() || null,
          employer:
            String(
              contact?.employer ||
                contact?.organization ||
                contact?.workplace ||
                contact?.current_company ||
                contact?.currentCompany ||
                ""
            ).trim() || null,
          organization:
            String(
              contact?.organization ||
                contact?.employer ||
                contact?.workplace ||
                contact?.current_company ||
                contact?.currentCompany ||
                ""
            ).trim() || null,
          workplace:
            String(
              contact?.workplace ||
                contact?.organization ||
                contact?.employer ||
                contact?.current_company ||
                contact?.currentCompany ||
                ""
            ).trim() || null,
          profile_image: normalizeContactImageUrl(contact?.profile_image || "") || null,
          source_url: safeUrl(contact?.source_url || "") || null,
          evidence: String(contact?.evidence || "").trim() || null,
        }))
        .filter((contact) => contact.name)
    : [];

  return {
    name: String(
      shouldTreatAsJobImport
        ? jobImport?.company_name || companyImport?.company_name || form.name || ""
        : companyImport?.company_name || jobImport?.company_name || form.name || ""
    ).trim(),
    website,
    industry,
    industry_sector: industry,
    size_range: sizeRange,
    company_size: sizeRange,
    location,
    status:
      shouldTreatAsJobImport && normalizedStatus === "New"
        ? "New"
        : normalizedStatus,
    source,
    notes: String(form.notes || "").trim() || null,
    owner_user_id: form.owner_user_id ? Number(form.owner_user_id) : null,
    email: companyEmail,
    phone: companyPhone,
    company_email: companyEmail,
    company_phone: companyPhone,
    linkedin_url: linkedinUrl,
    twitter_url: twitterUrl,
    facebook_url: facebookUrl,
    instagram_url: instagramUrl,
    youtube_url: youtubeUrl,
    tiktok_url: tiktokUrl,
    career_site: careerSite,
    career_website: careerSite,
    team_page_url: safeUrl(companyImport?.team_page_url || "") || null,
    company_link: companyLink,
    team_member: String(form.team_member || "").trim() || null,
    company_website: website,
    full_company_description: fullCompanyDescription,
    elevator_pitch: String(companyImport?.elevator_pitch || "").trim() || null,
    mission_and_vision: String(companyImport?.mission_and_vision || "").trim() || null,
    key_products_services: String(companyImport?.key_products_services || "").trim() || null,
    unique_selling_points: String(companyImport?.unique_selling_points || "").trim() || null,
    employee_perks: String(companyImport?.employee_perks || "").trim() || null,
    achievements_awards: String(companyImport?.achievements_awards || "").trim() || null,
    founded_year: String(companyImport?.founded_year || "").trim() || null,
    ownership_type: String(companyImport?.ownership_type || "").trim() || null,
    parent_company: String(companyImport?.parent_company || "").trim() || null,
    parent_company_subsidiaries: String(companyImport?.parent_company || "").trim() || null,
    work_policies: String(companyImport?.work_policies || "").trim() || null,
    recruitment_tools: recruitmentTools,
    logo,
    job_boards: jobBoards,
    job_board_rating: String(companyImport?.job_board_rating || "").trim() || null,
    imported_contacts: importedContacts,
    contact_source_policy: "team_leadership_only",
  };
}

function hasCareerLeadImportActivity(result = null) {
  return Boolean(
    Number(result?.inserted || 0) > 0 ||
      Number(result?.updated || 0) > 0 ||
      Number(result?.existing || 0) > 0
  );
}

function mergeCareerLeadImportCounts(...results) {
  return results.reduce(
    (acc, result) => ({
      inserted: acc.inserted + Number(result?.inserted || 0),
      updated: acc.updated + Number(result?.updated || 0),
      existing: acc.existing + Number(result?.existing || 0),
      skipped: acc.skipped + Number(result?.skipped || 0),
    }),
    { inserted: 0, updated: 0, existing: 0, skipped: 0 }
  );
}

function hasCareerJobImportActivity(result = null) {
  return Boolean(
    Number(result?.inserted || 0) > 0 ||
      Number(result?.updated || 0) > 0 ||
      Number(result?.existing || 0) > 0
  );
}

function mergeCareerJobImportCounts(...results) {
  return results.reduce(
    (acc, result) => ({
      inserted: acc.inserted + Number(result?.inserted || 0),
      updated: acc.updated + Number(result?.updated || 0),
      existing: acc.existing + Number(result?.existing || 0),
      skipped: acc.skipped + Number(result?.skipped || 0),
      createdJobIds: [
        ...acc.createdJobIds,
        ...((Array.isArray(result?.createdJobIds) ? result.createdJobIds : []).filter(Boolean)),
      ],
      updatedJobIds: [
        ...acc.updatedJobIds,
        ...((Array.isArray(result?.updatedJobIds) ? result.updatedJobIds : []).filter(Boolean)),
      ],
      existingJobIds: [
        ...acc.existingJobIds,
        ...((Array.isArray(result?.existingJobIds) ? result.existingJobIds : []).filter(Boolean)),
      ],
    }),
    {
      inserted: 0,
      updated: 0,
      existing: 0,
      skipped: 0,
      createdJobIds: [],
      updatedJobIds: [],
      existingJobIds: [],
    }
  );
}

function resolveCompanyProfileTabAfterCareerImport(importTarget = "leads") {
  return String(importTarget || "").toLowerCase() === "jobs" ? "jobs" : "leads";
}

function normalizeComparableUrlValue(value = "") {
  return String(safeUrl(value) || "")
    .replace(/\/+$/, "")
    .toLowerCase();
}

/* =========================
 * UI helpers (match mock)
 * ========================= */
const PURPLE = "#7C3AED";
const PURPLE_HOVER = "#6D28D9";
const SOFT_BG = "#F7F5FF";
const SOFT_BORDER = "#EAE5FF";
const LABEL = "#6B7280";

function Label({ children }) {
  return (
    <Typography data-popup-label sx={{ fontSize: 14, fontWeight: 500, lineHeight: 1, color: LABEL, mb: 0.6 }}>
      {children}
    </Typography>
  );
}

function Section({ children }) {
  return (
    <Typography data-popup-section sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px", color: PURPLE, letterSpacing: 0.2 }}>
      {children}
    </Typography>
  );
}

function PillButton({ sx, ...props }) {
  return (
    <Button
      {...props}
      className={withPillButtonClassName(props.className)}
      sx={[pillButtonBaseSx, pillButtonInteractiveSx, { fontWeight: 400, px: 2.1 }, sx || {}]}
    />
  );
}

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 10, bgcolor: "#fff" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: SOFT_BORDER },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#D7CBFF" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: PURPLE,
    borderWidth: 2,
  },
};

/* =========================================================
 * ✅ IMPORTANT: These MUST be outside the dialog component
 * to prevent remounting and input “deselect” behavior.
 * ======================================================= */
function CardShell({ title, onClose, saving, children }) {
  return (
    <Box data-popup-surface
      sx={{
        width: "100%",
        border: `1px solid ${SOFT_BORDER}`,
        borderRadius: 3,
        bgcolor: "#fff",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 4, pt: 4, pb: 2 }}>
        <Stack data-popup-header direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 26,
              height: 26,
              borderRadius: 1.5,
              display: "grid",
              placeItems: "center",
              border: `1px solid ${SOFT_BORDER}`,
              bgcolor: "#fff",
            }}
          >
            <BusinessOutlinedIcon sx={{ fontSize: 16, color: PURPLE }} />
          </Box>

          <Typography data-popup-title sx={{ fontSize: 20, fontWeight: 400, lineHeight: "35px", color: "#111827" }}>
            {title}
          </Typography>

          <Box sx={{ flex: 1 }} />

          <IconButton data-popup-close
            onClick={onClose}
            disabled={saving}
            sx={{
              width: 30,
              height: 30,
              borderRadius: 999,
              border: `1px solid ${SOFT_BORDER}`,
              bgcolor: "#fff",
            }}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>
      </Box>

      <Divider sx={{ borderColor: SOFT_BORDER }} />
      {children}
    </Box>
  );
}

function FooterBar({
  saving,
  leftText,
  onLeft,
  rightText,
  onRight,
  rightVariant = "contained",
  centerLeftText,
  onCenterLeft,
  centerRightText,
  onCenterRight,
}) {
  const leftIsBack = String(leftText || "").trim().toLowerCase() === "back";
  const rightIsNavigation = ["next", "continue"].includes(
    String(rightText || "").trim().toLowerCase()
  );
  const footerScrollSx = {
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
  };
  const footerRowSx = {
    width: "max-content",
    minWidth: "100%",
    flexWrap: "nowrap",
  };
  const footerButtonSx = {
    flexShrink: 0,
    whiteSpace: "nowrap",
  };

  return (
    <Box data-popup-footer sx={{ px: 4, pb: 4, pt: 2, ...footerScrollSx }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={footerRowSx}>
        <PillButton
          variant={leftIsBack ? "contained" : "outlined"}
          onClick={onLeft}
          disabled={saving}
          startIcon={leftIsBack ? <ArrowBackRoundedIcon /> : null}
          sx={{
            ...footerButtonSx,
            borderColor: leftIsBack ? PURPLE : SOFT_BORDER,
            color: leftIsBack ? "#fff" : PURPLE,
            bgcolor: leftIsBack ? PURPLE : "#fff",
            "&:hover": {
              bgcolor: leftIsBack ? PURPLE_HOVER : "#fff",
            },
            ...(leftIsBack ? popupNavigationPillSx : {}),
          }}
        >
          {leftText}
        </PillButton>

        <Box sx={{ flex: 1 }} />

        {!!centerLeftText && (
          <PillButton
            variant="outlined"
            onClick={onCenterLeft}
            disabled={saving}
            sx={{
              ...footerButtonSx,
              borderColor: SOFT_BORDER,
              color: PURPLE,
              bgcolor: "#fff",
            }}
          >
            {centerLeftText}
          </PillButton>
        )}

        {!!centerRightText && (
          <PillButton
            variant="outlined"
            onClick={onCenterRight}
            disabled={saving}
            sx={{
              ...footerButtonSx,
              borderColor: SOFT_BORDER,
              color: PURPLE,
              bgcolor: "#fff",
            }}
          >
            {centerRightText}
          </PillButton>
        )}

        <PillButton
          variant={rightVariant}
          onClick={onRight}
          disabled={saving}
          endIcon={rightIsNavigation ? <ArrowForwardRoundedIcon /> : null}
          sx={{
            ...footerButtonSx,
            bgcolor: rightVariant === "contained" ? PURPLE : "#fff",
            color: rightVariant === "contained" ? "#fff" : PURPLE,
            borderColor: SOFT_BORDER,
            "&:hover": { bgcolor: rightVariant === "contained" ? PURPLE_HOVER : "#fff" },
            minWidth: 110,
            ...(rightIsNavigation ? popupNavigationPillSx : {}),
          }}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {rightText}
        </PillButton>
      </Stack>
    </Box>
  );
}

function ChoiceRow({ icon, title, subtitle, onClick }) {
  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      sx={{
        border: `1px solid ${SOFT_BORDER}`,
        borderRadius: 2.5,
        bgcolor: "#fff",
        p: 1.5,
        cursor: "pointer",
        "&:hover": { boxShadow: "0 10px 28px rgba(124,58,237,0.12)" },
      }}
    >
      <Stack direction="row" spacing={1.25} alignItems="center">
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "grid",
            placeItems: "center",
            bgcolor: SOFT_BG,
            border: `1px solid ${SOFT_BORDER}`,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 900, color: "#111827" }}>
            {title}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "#6B7280", mt: 0.25 }}>
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

/* =========================
 * Main component
 * ========================= */
export default function AddCompanyDialog({
  open,
  onClose,
  onCreated,
  apiBase = API_BASE,
  user,
  token,
  initialValues = null,
  initialScreen = null,
  autoImportOnOpen = false,
  autoSubmitOnOpen = false,
  autoImportDiscoveredJobsOnOpen = false,
  openProfileOnComplete = true,
}) {
  const navigate = useNavigate();
  // Career-site openings discovered in the company flow are prospects. They
  // must be created as Leads; Jobs are created only through the dedicated Job flow.
  const normalizedAutoImportTarget = "leads";

  // start -> link -> overview -> additional -> leadPreview
  const [screen, setScreen] = useState("start");
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [parsingLink, setParsingLink] = useState(false);
  const [jobLinkImport, setJobLinkImport] = useState(null);
  const [companyLinkImport, setCompanyLinkImport] = useState(null);
  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });
  const [careerImportTarget, setCareerImportTarget] = useState(normalizedAutoImportTarget);
  const [createdCompanyForLeadFlow, setCreatedCompanyForLeadFlow] = useState(null);
  const [careerLeadPreviewRows, setCareerLeadPreviewRows] = useState([]);
  const [selectedCareerLeadIds, setSelectedCareerLeadIds] = useState([]);
  const [careerLeadPreviewWarnings, setCareerLeadPreviewWarnings] = useState([]);
  const [careerLeadPreviewMeta, setCareerLeadPreviewMeta] = useState({
    careerSite: "",
    scannedPages: 0,
    pagesConsidered: 0,
    previewSource: "",
    sourceLabel: "",
    bestSourceUrl: "",
  });
  const [loadingCareerLeadPreview, setLoadingCareerLeadPreview] = useState(false);
  const [importingCareerLeads, setImportingCareerLeads] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);
  const [teamMembersDebug, setTeamMembersDebug] = useState("");
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingCompanyDetails, setLoadingCompanyDetails] = useState(false);
  const [selectedExistingCompany, setSelectedExistingCompany] = useState(null);
  const autoImportAttemptedRef = useRef(false);
  const autoSubmitAttemptedRef = useRef(false);
  const seededCompanySelectionRef = useRef("");
  const continueFromInlineLinkRef = useRef(null);
  const submitOnOpenRef = useRef(null);

  const busy =
    saving ||
    parsingLink ||
    loadingCareerLeadPreview ||
    importingCareerLeads ||
    loadingCompanyDetails;

  const resetCareerLeadPreviewState = () => {
    setCreatedCompanyForLeadFlow(null);
    setCareerLeadPreviewRows([]);
    setSelectedCareerLeadIds([]);
    setCareerLeadPreviewWarnings([]);
    setCareerLeadPreviewMeta({
      careerSite: "",
      scannedPages: 0,
      pagesConsidered: 0,
      previewSource: "",
      sourceLabel: "",
      bestSourceUrl: "",
    });
    setLoadingCareerLeadPreview(false);
    setImportingCareerLeads(false);
  };

  useEffect(() => {
    if (!open) return;

    const defaultOwnerName = getUserDisplayName(user);
    const initialLinkAutofill = deriveLinkAutofill(
      initialValues && typeof initialValues === "object"
        ? initialValues.company_link || initialValues.website || initialValues.career_site || ""
        : "",
      "company"
    );
    const nextInitialScreen = normalizeDialogScreen(
      initialScreen,
      initialValues && typeof initialValues === "object" && String(initialValues.company_link || "").trim()
        ? "link"
        : "start"
    );
    setForm({
      ...DEFAULT_FORM,
      ...(initialValues && typeof initialValues === "object" ? initialValues : {}),
      status: normalizeCompanyStatusLabel(
        initialValues && typeof initialValues === "object"
          ? initialValues.status
          : DEFAULT_FORM.status,
        {
          fallback: DEFAULT_FORM.status,
          preserveUnknown: true,
        }
      ),
      owner_user_id:
        initialValues && typeof initialValues === "object" && initialValues.owner_user_id !== undefined
          ? initialValues.owner_user_id
          : user?.id ?? null,
      size_range: normalizeCompanySizeValue(
        initialValues && typeof initialValues === "object"
          ? initialValues.size_range || initialValues.company_size
          : DEFAULT_FORM.size_range
      ),
      source:
        initialValues && typeof initialValues === "object" && String(initialValues.source || "").trim()
          ? String(initialValues.source).trim()
          : initialLinkAutofill.source || DEFAULT_FORM.source,
      team_member:
        initialValues && typeof initialValues === "object" && initialValues.team_member !== undefined
          ? initialValues.team_member
          : defaultOwnerName,
    });
    setErrors({});
    setSaving(false);
    setParsingLink(false);
    setJobLinkImport(null);
    setCompanyLinkImport(null);
    setCareerImportTarget(normalizedAutoImportTarget);
    resetCareerLeadPreviewState();
    autoImportAttemptedRef.current = false;
    autoSubmitAttemptedRef.current = false;
    seededCompanySelectionRef.current = "";
    setSelectedExistingCompany(null);
    setScreen(nextInitialScreen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user?.id, initialScreen, initialValues]);

  const fetchTeamMembersList = useCallback(async () => {
    const authToken = token || getStoredToken();
    if (!authToken) {
      setTeamMembers([]);
      setTeamMembersDebug("No auth token");
      return;
    }

    setLoadingTeamMembers(true);
    setTeamMembersDebug("");

    try {
      const rows = await fetchTeamMembers({ apiBase, token: authToken });
      setTeamMembers(rows);
      setTeamMembersDebug(rows.length ? "" : "No team members found");
    } catch (error) {
      setTeamMembers([]);
      setTeamMembersDebug(error?.message || "Failed to load team members");
    } finally {
      setLoadingTeamMembers(false);
    }
  }, [apiBase, token]);

  useEffect(() => {
    if (!open) return;
    fetchTeamMembersList();
  }, [fetchTeamMembersList, open]);

  const companyCreateUrls = useMemo(
    () => buildEndpointUrls(apiBase, "/api/companies", "/companies"),
    [apiBase]
  );
  const companyListUrls = useMemo(
    () => buildEndpointUrls(apiBase, "/api/companies?page=1&limit=5000&compact=1", "/companies?page=1&limit=5000&compact=1"),
    [apiBase]
  );
  const companyParseUrls = useMemo(
    () => buildEndpointUrls(apiBase, "/api/companies/parse-link", "/companies/parse-link"),
    [apiBase]
  );
  const jobParseUrls = useMemo(
    () => buildEndpointUrls(apiBase, "/api/jobs/parse-link", "/jobs/parse-link"),
    [apiBase]
  );
  const buildCompanyDetailUrls = useCallback(
    (companyId) =>
      buildEndpointUrls(
        apiBase,
        `/api/companies/${encodeURIComponent(String(companyId))}`,
        `/companies/${encodeURIComponent(String(companyId))}`
      ),
    [apiBase]
  );
  const buildCareerPreviewUrls = useCallback(
    (companyId) =>
      buildEndpointUrls(
        apiBase,
        `/api/companies/${encodeURIComponent(String(companyId))}/career-jobs/preview`,
        `/companies/${encodeURIComponent(String(companyId))}/career-jobs/preview`
      ),
    [apiBase]
  );

  useEffect(() => {
    if (!open) return undefined;

    let active = true;

    const loadCompanies = async () => {
      setLoadingCompanies(true);

      try {
        const authToken = token || getStoredToken();
        const { response, data } = await requestJsonWithFallback({
          urls: companyListUrls,
          method: "GET",
          token: authToken,
        });

        if (!response.ok) {
          throw new Error(data?.error || `Failed to load companies (${response.status})`);
        }

        const rows = Array.isArray(data) ? data : data?.rows || [];
        const nextCompanies = rows
          .map((row) => buildCompanyOption(row))
          .filter(Boolean)
          .sort((left, right) =>
            String(left?.company_name || "").localeCompare(String(right?.company_name || ""))
          );

        if (active) {
          setCompanies(nextCompanies);
        }
      } catch (error) {
        if (active) {
          console.error("Failed to load companies for add-company dialog:", error);
          setCompanies([]);
        }
      } finally {
        if (active) {
          setLoadingCompanies(false);
        }
      }
    };

    loadCompanies();

    return () => {
      active = false;
    };
  }, [companyListUrls, open, token]);
  const buildCareerImportUrls = useCallback(
    (companyId) =>
      buildEndpointUrls(
        apiBase,
        `/api/companies/${encodeURIComponent(String(companyId))}/career-leads/import`,
        `/companies/${encodeURIComponent(String(companyId))}/career-leads/import`
      ),
    [apiBase]
  );

  const summaryLine = useMemo(() => {
    const bits = [
      form.name?.trim(),
      form.location?.trim(),
      form.industry?.trim(),
      form.size_range?.trim(),
    ].filter(Boolean);
    return bits.length ? `Info: ${bits.join(", ")}` : "Info:";
  }, [
    form.name,
    form.location,
    form.industry,
    form.size_range,
  ]);

  const selectedTeamMember = useMemo(() => {
    const typed = normalizeTeamMemberName(form.team_member);
    if (!typed) return null;

    const byName = teamMembers.find((item) => item.name.toLowerCase() === typed.toLowerCase());
    if (byName) return byName;

    return {
      id: null,
      name: typed,
    };
  }, [form.team_member, teamMembers]);

  const assignTeamOptions = teamMembers;
  const assignTeamLoading = loadingTeamMembers;
  const selectedExistingCompanyId = useMemo(() => {
    const nextValue = Number(selectedExistingCompany?.company_id || selectedExistingCompany?.id || 0);
    return Number.isFinite(nextValue) && nextValue > 0 ? nextValue : null;
  }, [selectedExistingCompany?.company_id, selectedExistingCompany?.id]);

  const selectedCompanyOptionValue = useMemo(() => {
    if (!selectedExistingCompany?.company_id) return null;

    return {
      ...selectedExistingCompany,
      company_id: selectedExistingCompanyId,
    };
  }, [selectedExistingCompany, selectedExistingCompanyId]);

  const filterCompanyOptions = useCallback((options, params) => {
    const inputValue = normalizeCompanyName(params.inputValue);
    const normalizedInput = normalizeLookupValue(inputValue);
    const filtered = options.filter((option) => {
      if (!normalizedInput) return true;

      const searchableText = [
        option?.company_name,
        option?.website,
        option?.industry,
        option?.location,
      ]
        .map((value) => normalizeLookupValue(value))
        .filter(Boolean)
        .join(" ");

      return searchableText.includes(normalizedInput);
    });
    const base = companyFilter(filtered, {
      ...params,
      inputValue: "",
    });
    const exists = options.some(
      (option) => normalizeLookupValue(option?.company_name || "") === normalizedInput
    );

    if (inputValue && !exists) {
      base.push({
        inputValue,
        company_id: null,
        company_name: inputValue,
        company_logo: "",
        website: "",
      });
    }

    return dedupeAutocompleteOptions(base, (option) =>
      normalizeAutocompleteText(option?.company_name || option?.inputValue || "")
    );
  }, []);

  const applyExistingCompanySelection = useCallback(
    async (optionOrCompany) => {
      const baseOption = buildCompanyOption(optionOrCompany);
      const companyId = Number(
        baseOption?.company_id ?? optionOrCompany?.company_id ?? optionOrCompany?.id ?? 0
      );

      if (!Number.isFinite(companyId) || companyId <= 0) {
        setSelectedExistingCompany(null);
        return null;
      }

      const authToken = token || getStoredToken();
      setLoadingCompanyDetails(true);
      try {
        const { response, data } = await requestJsonWithFallback({
          urls: buildCompanyDetailUrls(companyId),
          method: "GET",
          token: authToken,
        });

        if (!response.ok) {
          throw new Error(data?.error || `Failed to load company details (${response.status})`);
        }

        const companyRecord =
          data?.company && typeof data.company === "object" ? data.company : optionOrCompany || {};
        const nextOption = buildCompanyOption(companyRecord) || baseOption;
        const nextCompanyImport = normalizeParsedCompanyImport(companyRecord);
        const nextFormState = buildExistingCompanyFormState(form, companyRecord);

        setCompanies((prev) => upsertCompanyOption(prev, nextOption));
        setSelectedExistingCompany({
          ...(nextOption || {}),
          company_id: companyId,
          companyRecord,
        });
        setCompanyLinkImport(nextCompanyImport);
        setJobLinkImport(null);
        resetCareerLeadPreviewState();
        setCareerImportTarget(normalizedAutoImportTarget);
        setForm(nextFormState);
        setErrors((prev) => {
          const next = { ...prev };
          delete next.name;
          delete next.website;
          delete next.career_site;
          return next;
        });

        return companyRecord;
      } finally {
        setLoadingCompanyDetails(false);
      }
    },
    [buildCompanyDetailUrls, form, normalizedAutoImportTarget, token]
  );

  useEffect(() => {
    // An automated Use Link run already carries the active company context in
    // initialValues. Selecting the stored company here would overwrite the
    // pasted careers/job URL before it is scanned.
    if (!open || autoImportOnOpen || selectedExistingCompanyId || !companies.length) return;

    const seededCompanyId = Number(initialValues?.company_id ?? initialValues?.companyId ?? 0);
    if (!Number.isFinite(seededCompanyId) || seededCompanyId <= 0) return;

    const matchedOption = companies.find(
      (option) => Number(option?.company_id || 0) === seededCompanyId
    );

    if (!matchedOption?.company_id) return;

    const seedKey = `${matchedOption.company_id}`;
    if (seededCompanySelectionRef.current === seedKey) return;
    seededCompanySelectionRef.current = seedKey;

    applyExistingCompanySelection(matchedOption).catch((error) => {
      console.error("Failed to prefill existing company in add-company dialog:", error);
    });
  }, [
    applyExistingCompanySelection,
    companies,
    initialValues,
    open,
    autoImportOnOpen,
    selectedExistingCompanyId,
  ]);

  const companyLinkSummary = useMemo(() => {
    if (jobLinkImport?.job_title) {
      const details = [
        `Job: ${jobLinkImport.job_title}`,
        jobLinkImport.company_name ? `Company: ${jobLinkImport.company_name}` : "",
        jobLinkImport.source ? `Source: ${jobLinkImport.source}` : "",
        companyLinkImport?.industry_sector ? `Industry: ${companyLinkImport.industry_sector}` : "",
      ].filter(Boolean);
      return `${details.join(" | ")}. We will create the company, map the profile fields, save leadership contacts, scan the career site, let you select openings to add as jobs, and then open the company profile.`;
    }

    if (companyLinkImport) {
      const details = [
        companyLinkImport.company_name ? `Company: ${companyLinkImport.company_name}` : "",
        companyLinkImport.industry_sector ? `Industry: ${companyLinkImport.industry_sector}` : "",
        companyLinkImport.location ? `Location: ${companyLinkImport.location}` : "",
        companyLinkImport.company_website ? `Website: ${companyLinkImport.company_website}` : "",
      ].filter(Boolean);
      if (details.length) {
        return `${details.join(" | ")}. We will create the company, map the company profile, save team and leadership contacts, scan its careers page for openings, let you choose openings to import as jobs, and then open the company profile.`;
      }
    }

    const summary = describeLinkAutofill(deriveLinkAutofill(form.company_link, "company"), {
      includeWebsite: true,
    });
    if (isLikelyJobPostingLink(form.company_link)) {
      return summary
        ? `${summary} | Job posting detected. We will import the company details, save leadership contacts, scan for openings, let you choose which ones become jobs, and then open the company profile.`
        : "Job posting detected. We will import the company details, save leadership contacts, scan for openings, let you choose which ones become jobs, and then open the company profile.";
    }
    return (
      summary ||
      "We will scan the site, map the company profile, save leadership contacts, show the current openings, and then open the company profile."
    );
  }, [form.company_link, jobLinkImport, companyLinkImport]);

  useEffect(() => {
    setErrors((prev) => {
      if (!prev || typeof prev !== "object" || !Object.keys(prev).length) {
        return prev;
      }

      let changed = false;
      const next = { ...prev };
      const clearIfResolved = (key, resolved) => {
        if (resolved && Object.prototype.hasOwnProperty.call(next, key)) {
          delete next[key];
          changed = true;
        }
      };

      if (selectedExistingCompanyId) {
        ["name", "website", "status", "source", "_form"].forEach((key) => {
          if (Object.prototype.hasOwnProperty.call(next, key)) {
            delete next[key];
            changed = true;
          }
        });
        return changed ? next : prev;
      }

      clearIfResolved("name", Boolean(String(form.name || "").trim()));
      clearIfResolved("website", Boolean(String(form.website || "").trim()));
      clearIfResolved("status", Boolean(String(form.status || "").trim()));
      clearIfResolved("source", Boolean(String(form.source || "").trim()));

      if (changed && Object.prototype.hasOwnProperty.call(next, "_form")) {
        delete next._form;
      }

      return changed ? next : prev;
    });
  }, [form.name, form.source, form.status, form.website, selectedExistingCompanyId]);

  const sourceOptions = useMemo(() => {
    const currentValue = String(jobLinkImport?.source || form.source || "").trim();
    return Array.from(new Set([...COMPANY_SOURCE_OPTIONS, currentValue].filter(Boolean)));
  }, [form.source, jobLinkImport?.source]);

  const careerLeadPreviewColumns = useMemo(
    () => [
      { id: "job_title", label: "Job Title" },
      { id: "location", label: "Location" },
      { id: "employment_type", label: "Type" },
      { id: "confidence", label: "Confidence" },
      { id: "job_url", label: "Source", sortable: false },
    ],
    []
  );

  const setField = (key) => (e) => {
    const value = e.target.value;
    const normalizedValue =
      key === "name"
        ? normalizeCompanyName(value)
        : key === "industry"
          ? clampText(value, COMPANY_INDUSTRY_MAX)
          : key === "size_range"
            ? normalizeCompanySizeValue(value)
            : value;

    if (
      selectedExistingCompanyId &&
      [
        "name",
        "website",
        "industry",
        "size_range",
        "location",
        "career_site",
        "email",
        "phone",
      ].includes(key)
    ) {
      setSelectedExistingCompany(null);
      setCompanyLinkImport(null);
    }

    setForm((p) => ({
      ...p,
      [key]: normalizedValue,
    }));
    setErrors((p) => {
      const next = { ...p };
      delete next[key];
      delete next._form;
      return next;
    });
  };

  const setTeamMemberValue = useCallback((value) => {
    const nextValue = normalizeTeamMemberName(value);
    setForm((prev) => ({
      ...prev,
      team_member: nextValue,
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.team_member;
      delete next._form;
      return next;
    });
  }, []);

  const ensureTeamMemberValue = useCallback(
    async (value, authToken) => {
      const normalizedValue = normalizeTeamMemberName(value);
      if (!normalizedValue) return "";

      const ensuredTeamMember = await createTeamMemberIfMissing({
        apiBase,
        token: authToken,
        name: normalizedValue,
        existing: teamMembers,
      });
      const finalValue = ensuredTeamMember?.name || normalizedValue;
      setTeamMembers((prev) =>
        upsertTeamMemberOption(prev, ensuredTeamMember || { name: finalValue })
      );
      return finalValue;
    },
    [apiBase, teamMembers]
  );

  const handleCompanyLinkChange = (e) => {
    const nextValue = e?.target?.value || "";
    const autofill = deriveLinkAutofill(nextValue, "company");
    setJobLinkImport(null);
    setCompanyLinkImport(null);
    setSelectedExistingCompany(null);
    resetCareerLeadPreviewState();

    setForm((prev) => {
      const next = {
        ...prev,
        company_link: nextValue,
      };

      if (autofill.source) next.source = autofill.source;
      if (String(autofill.companyName || "").trim().length > 1) next.name = autofill.companyName;
      if (autofill.website) next.website = autofill.website;

      return next;
    });

    setErrors((prev) => {
      const next = { ...prev };
      delete next.company_link;
      return next;
    });
  };

  const openCompanyProfile = (companyRecord, defaultTab = "details") => {
    const companyId = companyRecord?.id;
    if (!companyId) {
      onClose?.();
      return;
    }

    const payload = buildOpenCompanyNavPayload({
      company: companyRecord,
      defaultTab,
      returnListTab: "companies",
      entityType: "company",
    });
    if (!payload) {
      onClose?.();
      return;
    }

    navigate(
      `/companies?companyId=${encodeURIComponent(companyId)}&tab=${encodeURIComponent(defaultTab)}&entityType=company&returnTab=companies`,
      {
        state: payload,
      }
    );
    onClose?.();
  };

  const saveCompanyRecord = async (
    companyPayload,
    authToken,
    { interactiveImport = false } = {}
  ) => {
    setSaving(true);
    try {
      const fd = new FormData();
      const requestPayload = interactiveImport
        ? {
            ...companyPayload,
            interactive_import: true,
          }
        : companyPayload;
      fd.append("data", JSON.stringify(requestPayload));

      const { response: res, data } = await requestJsonWithFallback({
        urls: companyCreateUrls,
        method: "POST",
        token: authToken,
        body: fd,
        isFormData: true,
      });

      if (!res.ok) {
        throw new Error(data?.error || res.statusText || "Failed to create company");
      }

      const companyId = data?.companyId || data?.id || data?.insertId;
      const alreadyExists = /company already exists/i.test(String(data?.message || ""));
      return {
        companyId,
        alreadyExists,
        company: {
          id: companyId,
          ...companyPayload,
          ...(data?.company && typeof data.company === "object" ? data.company : {}),
          entityType: "company",
          logo:
            data?.logo ||
            data?.company?.logo ||
            companyPayload.logo ||
            null,
        },
      };
    } finally {
      setSaving(false);
    }
  };

  const finishInteractiveUseLinkImport = async (companyRecord) => {
    await onCreated?.(companyRecord);
    // Link imports are review-first. Do not leave the user in the company
    // preview/profile flow; send them to the board where the AI proposals are
    // reviewed and approved.
    onClose?.();
    navigate("/activities");
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("rpo:activities-refresh"));
    }
  };

  const completeSelectionOnly = useCallback(
    async (companyRecord) => {
      if (!companyRecord || typeof companyRecord !== "object") {
        throw new Error("Missing company record.");
      }

      await onCreated?.(companyRecord);
      onClose?.();
    },
    [onClose, onCreated]
  );

  const previewCareerLeadsForCompany = async (
    companyRecord,
    authToken,
    { openPreview = true } = {}
  ) => {
    const companyId = companyRecord?.id;
    if (!companyId) return { opened: false, reason: "missing-company" };

    setLoadingCareerLeadPreview(true);
    try {
      const { response: res, data } = await requestJsonWithFallback({
        urls: buildCareerPreviewUrls(companyId),
        method: "POST",
        token: authToken,
        body: {
          company_name: companyRecord?.name || form.name || "",
          website:
            normalizeCompanyWebsiteUrl(
              companyRecord?.website || companyRecord?.company_website || form.website
            ) || "",
          career_site:
            normalizeCompanyWebsiteUrl(
              companyRecord?.career_site ||
                companyRecord?.career_website ||
                form.career_site ||
                form.company_link
            ) || "",
          company_link: safeUrl(form.company_link || companyRecord?.company_link || "") || "",
          // Fetch the full careers listing rather than stopping after the
          // first few result pages. The API enforces its own safety limits.
          max_jobs: 250,
          max_pages: 50,
        },
      });

      if (!res.ok) {
        throw new Error(
          data?.error || res.statusText || "Failed to scan the company career site"
        );
      }

      const rows = Array.isArray(data?.jobs)
        ? data.jobs
            .map((job, index) => ({
              ...job,
              id: String(job?.id || job?.job_url || `${job?.job_title || "job"}-${index + 1}`),
            }))
            .filter((job) => String(job?.job_title || "").trim())
        : [];
      const warnings = [
        ...(Array.isArray(data?.warnings) ? data.warnings : []),
        ...(Array.isArray(data?.fetchErrors) ? data.fetchErrors : []),
      ]
        .map((item) => formatCareerLeadPreviewIssue(item))
        .filter(Boolean);

      const nextCompanyRecord = {
        ...companyRecord,
        ...(data?.company && typeof data.company === "object" ? data.company : {}),
      };

      if (!rows.length) {
        return {
          opened: false,
          jobsCount: 0,
          warnings,
          careerSite: String(data?.career_site || "").trim(),
          companyRecord: nextCompanyRecord,
        };
      }

      setCreatedCompanyForLeadFlow(nextCompanyRecord);
      setCareerLeadPreviewRows(rows);
      setSelectedCareerLeadIds(rows.map((row) => String(row.id)));
      setCareerLeadPreviewWarnings(warnings);
      setCareerLeadPreviewMeta({
        careerSite: String(data?.career_site || "").trim(),
        scannedPages: Number(data?.scanned_pages || 0),
        pagesConsidered: Number(data?.pages_considered || 0),
        previewSource: String(data?.preview_source || "").trim(),
        sourceLabel: String(data?.preview_source_label || "").trim(),
        bestSourceUrl: String(data?.best_source_url || "").trim(),
      });
      if (openPreview) {
        setScreen("leadPreview");
      }

      return {
        opened: true,
        jobsCount: rows.length,
        warnings,
        rows,
        companyRecord: nextCompanyRecord,
      };
    } finally {
      setLoadingCareerLeadPreview(false);
    }
  };

  const buildCareerLeadImportMessage = ({
    inserted = 0,
    updated = 0,
    existing = 0,
    skipped = 0,
    successPrefix = "Company saved",
  } = {}) => {
    const prefix = String(successPrefix || "Company saved").trim() || "Company saved";
    if (inserted > 0 || updated > 0) {
      return `${prefix}. Added ${inserted} lead${inserted === 1 ? "" : "s"}${updated > 0 ? `, refreshed ${updated}` : ""}${existing > 0 ? `, matched ${existing} existing` : ""}${skipped > 0 ? `, skipped ${skipped}` : ""}.`;
    }
    if (existing > 0) {
      return `${prefix}. Matched ${existing} existing lead${existing === 1 ? "" : "s"}.`;
    }
    return `${prefix}. No new leads were added.`;
  };

  const buildCareerJobImportMessage = ({
    inserted = 0,
    updated = 0,
    existing = 0,
    skipped = 0,
    successPrefix = "Company saved",
  } = {}) => {
    const prefix = String(successPrefix || "Company saved").trim() || "Company saved";
    if (inserted > 0 || updated > 0) {
      return `${prefix}. Added ${inserted} job${inserted === 1 ? "" : "s"}${updated > 0 ? `, refreshed ${updated}` : ""}${existing > 0 ? `, matched ${existing} existing` : ""}${skipped > 0 ? `, skipped ${skipped}` : ""}.`;
    }
    if (existing > 0) {
      return `${prefix}. Matched ${existing} existing job${existing === 1 ? "" : "s"}.`;
    }
    return `${prefix}. No new jobs were added.`;
  };

  const performCareerLeadImport = async ({
    companyRecord,
    jobs,
    authToken,
  }) => {
    const companyId = companyRecord?.id;
    if (!companyId) {
      throw new Error("Missing company id for career lead import.");
    }

    const selectedJobs = Array.isArray(jobs) ? jobs.filter(Boolean) : [];
    if (!selectedJobs.length) {
      throw new Error("Select at least one opening to add as leads, or skip for now.");
    }

    setImportingCareerLeads(true);
    try {
      const { response: res, data } = await requestJsonWithFallback({
        urls: buildCareerImportUrls(companyId),
        method: "POST",
        token: authToken,
        // A job link supplied while creating a company is prospecting input.
        // It must always create a Lead, never an internal Job record.
        body: { jobs: selectedJobs, import_target: "leads" },
      });

      if (!res.ok) {
        throw new Error(data?.error || res.statusText || "Failed to import selected leads");
      }

      const nextCompanyRecord = {
        ...companyRecord,
        ...(data?.company && typeof data.company === "object" ? data.company : {}),
      };

      return {
        nextCompanyRecord,
        inserted: Number(data?.inserted || 0),
        updated: Number(data?.updated || 0),
        existing: Number(data?.existing || 0),
        skipped: Number(data?.skipped || 0),
      };
    } finally {
      setImportingCareerLeads(false);
    }
  };

  const performCareerJobImport = async ({
    companyRecord,
    jobs,
    authToken,
  }) => {
    const companyId = companyRecord?.id;
    if (!companyId) {
      throw new Error("Missing company id for career job import.");
    }

    const selectedJobs = Array.isArray(jobs) ? jobs.filter(Boolean) : [];
    if (!selectedJobs.length) {
      throw new Error("Select at least one opening to add as jobs, or skip for now.");
    }

    setImportingCareerLeads(true);
    try {
      const { response: res, data } = await requestJsonWithFallback({
        urls: buildCareerImportUrls(companyId),
        method: "POST",
        token: authToken,
        body: {
          jobs: selectedJobs,
          import_target: "jobs",
        },
      });

      if (!res.ok) {
        throw new Error(data?.error || res.statusText || "Failed to import selected jobs");
      }

      const nextCompanyRecord = {
        ...companyRecord,
        ...(data?.company && typeof data.company === "object" ? data.company : {}),
      };

      return {
        nextCompanyRecord,
        inserted: Number(data?.inserted || 0),
        updated: Number(data?.updated || 0),
        existing: Number(data?.existing || 0),
        skipped: Number(data?.skipped || 0),
        createdJobIds: Array.isArray(data?.created_job_ids) ? data.created_job_ids : [],
        updatedJobIds: Array.isArray(data?.updated_job_ids) ? data.updated_job_ids : [],
        existingJobIds: Array.isArray(data?.existing_job_ids) ? data.existing_job_ids : [],
      };
    } finally {
      setImportingCareerLeads(false);
    }
  };

  const finalizeCompanyFlow = async ({
    companyRecord,
    authToken,
    successMessage,
    runCareerLeadPreview = false,
    openPreviewOnFound = false,
    initialJobsToImport = [],
    importTarget = normalizedAutoImportTarget,
  }) => {
    const normalizedImportTarget =
      String(importTarget || "").toLowerCase() === "jobs" ? "jobs" : "leads";
    const performCareerImport =
      normalizedImportTarget === "jobs" ? performCareerJobImport : performCareerLeadImport;
    const buildCareerImportMessage =
      normalizedImportTarget === "jobs"
        ? buildCareerJobImportMessage
        : buildCareerLeadImportMessage;
    const hasCareerImportActivity =
      normalizedImportTarget === "jobs"
        ? hasCareerJobImportActivity
        : hasCareerLeadImportActivity;
    const mergeCareerImportCounts =
      normalizedImportTarget === "jobs"
        ? mergeCareerJobImportCounts
        : mergeCareerLeadImportCounts;
    const importedEntityLabel = normalizedImportTarget === "jobs" ? "jobs" : "leads";
    const importedEntitySingularLabel =
      normalizedImportTarget === "jobs" ? "job" : "lead";
    const shouldOpenCareerLeadPreview =
      !autoImportDiscoveredJobsOnOpen && (openPreviewOnFound || autoImportOnOpen || autoSubmitOnOpen);
    let nextCompanyRecord = companyRecord;
    let initialJobImportResult = null;

    onCreated?.(nextCompanyRecord);

    if (Array.isArray(initialJobsToImport) && initialJobsToImport.length) {
      try {
        initialJobImportResult = await performCareerImport({
          companyRecord: nextCompanyRecord,
          jobs: initialJobsToImport,
          authToken,
        });
        nextCompanyRecord = initialJobImportResult.nextCompanyRecord;
        onCreated?.(nextCompanyRecord);
      } catch (error) {
        setToast({
          open: true,
          type: "warning",
          msg:
            error?.message ||
            `The company was saved, but the provided ${importedEntitySingularLabel} posting could not be imported automatically.`,
        });
      }
    }

    if (runCareerLeadPreview) {
      try {
        const previewResult = await previewCareerLeadsForCompany(nextCompanyRecord, authToken, {
          openPreview: shouldOpenCareerLeadPreview,
        });
        if (previewResult?.companyRecord) {
          nextCompanyRecord = previewResult.companyRecord;
          onCreated?.(nextCompanyRecord);
        }
        if (previewResult?.opened) {
          if (autoImportDiscoveredJobsOnOpen) {
            try {
              const importResult = await performCareerImport({
                companyRecord: previewResult?.companyRecord || nextCompanyRecord,
                jobs: previewResult?.rows || [],
                authToken,
              });
              onCreated?.(importResult.nextCompanyRecord);
              const combinedImportResult = mergeCareerImportCounts(
                initialJobImportResult,
                importResult
              );
              setToast({
                open: true,
                type: "success",
                msg: buildCareerImportMessage({
                  ...combinedImportResult,
                  successPrefix: successMessage,
                }),
              });
              openCompanyProfile(
                importResult.nextCompanyRecord,
                resolveCompanyProfileTabAfterCareerImport(normalizedImportTarget)
              );
              return;
            } catch (importError) {
              setScreen("leadPreview");
              setToast({
                open: true,
                type: "warning",
                msg:
                  importError?.message ||
                  `The company was saved and openings were found, but the automatic ${importedEntitySingularLabel} import could not finish. Review the openings and import them manually.`,
              });
              return;
            }
          }

          if (shouldOpenCareerLeadPreview) {
            setToast({
              open: true,
              type: "success",
              msg: hasCareerImportActivity(initialJobImportResult)
                ? `${buildCareerImportMessage({
                    ...initialJobImportResult,
                    successPrefix: successMessage,
                  })} Select any additional openings you want to add as ${importedEntityLabel}.`
                : `${successMessage}. Select the openings you want to add as ${importedEntityLabel}.`,
            });
            return;
          }

          setToast({
            open: true,
            type: "success",
            msg: hasCareerImportActivity(initialJobImportResult)
              ? `${buildCareerImportMessage({
                  ...initialJobImportResult,
                  successPrefix: successMessage,
                })} Found ${previewResult.rows?.length || 0} additional opening${
                  previewResult.rows?.length === 1 ? "" : "s"
                } on the career site. Opening the company profile.`
              : `${successMessage}. Found ${previewResult.rows?.length || 0} opening${
                  previewResult.rows?.length === 1 ? "" : "s"
                } on the career site. Opening the company profile.`,
          });
          openCompanyProfile(
            nextCompanyRecord,
            resolveCompanyProfileTabAfterCareerImport(normalizedImportTarget)
          );
          return;
        }

        const warningMessage = hasCareerImportActivity(initialJobImportResult)
          ? `${buildCareerImportMessage({
              ...initialJobImportResult,
              successPrefix: successMessage,
            })} No additional openings were found. Opening the company profile.`
          : previewResult?.warnings?.[0] ||
            (previewResult?.careerSite
              ? `Company saved, but no openings were found at ${previewResult.careerSite}. Opening the company profile.`
              : `${successMessage}. No openings were found to import. Opening the company profile.`);
        setToast({
          open: true,
          type:
            hasCareerImportActivity(initialJobImportResult) || !previewResult?.warnings?.length
              ? "success"
              : "warning",
          msg: warningMessage,
        });
        openCompanyProfile(
          nextCompanyRecord,
          resolveCompanyProfileTabAfterCareerImport(normalizedImportTarget)
        );
        return;
      } catch (error) {
        setToast({
          open: true,
          type: hasCareerImportActivity(initialJobImportResult) ? "success" : "warning",
          msg:
            hasCareerImportActivity(initialJobImportResult)
              ? `${buildCareerImportMessage({
                  ...initialJobImportResult,
                  successPrefix: successMessage,
                })} The additional company scan could not finish. Opening the company profile.`
              : error?.message ||
                "Company saved, but the career-site scan could not finish. Opening the company profile.",
        });
        openCompanyProfile(
          nextCompanyRecord,
          resolveCompanyProfileTabAfterCareerImport(normalizedImportTarget)
        );
        return;
      }
    }

    setToast({
      open: true,
      type: "success",
      msg: hasCareerImportActivity(initialJobImportResult)
        ? buildCareerImportMessage({
            ...initialJobImportResult,
            successPrefix: successMessage,
          })
        : successMessage,
    });
    openCompanyProfile(
      nextCompanyRecord,
      resolveCompanyProfileTabAfterCareerImport(normalizedImportTarget)
    );
  };

  const handleImportCareerLeads = async () => {
    const companyRecord = createdCompanyForLeadFlow;
    const companyId = companyRecord?.id;
    if (!companyId) return;
    const normalizedImportTarget =
      String(careerImportTarget || "").toLowerCase() === "jobs" ? "jobs" : "leads";
    const performCareerImport =
      normalizedImportTarget === "jobs" ? performCareerJobImport : performCareerLeadImport;
    const buildCareerImportMessage =
      normalizedImportTarget === "jobs"
        ? buildCareerJobImportMessage
        : buildCareerLeadImportMessage;
    const importedEntityLabel = normalizedImportTarget === "jobs" ? "jobs" : "leads";
    const selectedJobs = careerLeadPreviewRows.filter((row) =>
      selectedCareerLeadIds.includes(String(row.id))
    );
    if (!selectedJobs.length) {
      setToast({
        open: true,
        type: "warning",
        msg: `Select at least one opening to add as ${importedEntityLabel}, or skip for now.`,
      });
      return;
    }

    const authToken = token || getStoredToken();
    try {
      const importResult = await performCareerImport({
        companyRecord,
        jobs: selectedJobs,
        authToken,
      });
      onCreated?.(importResult.nextCompanyRecord);
      setToast({
        open: true,
        type: "success",
        msg: buildCareerImportMessage(importResult),
      });
      openCompanyProfile(
        importResult.nextCompanyRecord,
        resolveCompanyProfileTabAfterCareerImport(normalizedImportTarget)
      );
    } catch (error) {
      console.error("Failed to import company career leads:", error);
      setToast({
        open: true,
        type: "error",
        msg:
          `The opening import could not be completed. ${
            error?.message || `No ${importedEntityLabel} were added.`
          }`,
      });
    }
  };

  const handleSkipCareerLeads = () => {
    if (!createdCompanyForLeadFlow) return;
    setToast({
      open: true,
      type: "info",
      msg:
        careerImportTarget === "jobs"
          ? "Company saved. You can add career-site jobs later from the company Jobs tab."
          : "Company saved. You can add career-site jobs later from the company profile.",
    });
    openCompanyProfile(createdCompanyForLeadFlow);
  };

  const handleContinueFromInlineLink = async () => {
    const linkValue = String(form.company_link || "").trim();
    if (!linkValue) {
      setErrors((prev) => ({ ...prev, company_link: "Please enter a company link." }));
      return;
    }

    if (safeUrl(linkValue) === null) {
      setErrors((prev) => ({ ...prev, company_link: "Enter a valid link URL" }));
      return;
    }

    const normalizedLink = safeUrl(linkValue) || linkValue;
    const authToken = token || getStoredToken();
    // This is intentionally before parsing/saving. A Use Link import is an
    // asynchronous activity workflow, so the user should see the Activities
    // board immediately rather than waiting for the company scan to finish.
    onClose?.();
    navigate("/activities");
    if (typeof window !== "undefined") {
      const pendingCompanyName = String(form.name || "").trim() || "company";
      sessionStorage.setItem(
        "rpo:pending-company-enrichment",
        JSON.stringify({ name: pendingCompanyName, startedAt: Date.now() })
      );
      window.dispatchEvent(new CustomEvent("rpo:activities-refresh"));
    }
    const isJobLink = isLikelyJobPostingLink(normalizedLink);
    const linkAutofill = deriveLinkAutofill(normalizedLink, isJobLink ? "job" : "company");
    const inputSocialHints = buildSocialImportFromUrl(normalizedLink);
    const normalizedLinkIdentity = normalizeComparableUrlValue(normalizedLink);
    const seededComparableUrls = [
      initialValues?.company_link,
      initialValues?.website,
      initialValues?.career_site,
    ]
      .map((value) => normalizeComparableUrlValue(value))
      .filter(Boolean);
    const seededCompanyId = Number(initialValues?.company_id ?? initialValues?.companyId ?? 0);
    const hasScopedCompanyContext = Number.isFinite(seededCompanyId) && seededCompanyId > 0;
    const hasExplicitOverrideLink = Boolean(
      normalizedLinkIdentity &&
        seededComparableUrls.length &&
        !seededComparableUrls.includes(normalizedLinkIdentity) &&
        !hasScopedCompanyContext
    );
    const parseCompanyNameHint = hasExplicitOverrideLink
      ? ""
      : String(form.name || linkAutofill.companyName || "").trim();
    const parseCompanyWebsiteHint = hasExplicitOverrideLink
      ? ""
      : normalizeCompanyWebsiteUrl(form.website) ||
        normalizeCompanyWebsiteUrl(linkAutofill.website) ||
        "";
    const parseCareerSiteHint = hasExplicitOverrideLink
      ? ""
      : normalizeCompanyWebsiteUrl(form.career_site) || "";
    const mergeBaseFormState = hasExplicitOverrideLink
      ? {
          ...form,
          name: "",
          website: "",
          career_site: "",
          location: "",
          email: "",
          phone: "",
        }
      : form;
    // A pasted job posting is a prospecting lead in the company-creation flow. Do not
    // force it into the Jobs tab; the dedicated Quick Actions -> Add Job flow remains
    // the explicit path for creating assigned jobs.
    const effectiveImportTarget = normalizedAutoImportTarget;

    setParsingLink(true);
    setCareerImportTarget(effectiveImportTarget);
    try {
      const companyProfileRequest = requestJsonWithFallback({
        urls: companyParseUrls,
        method: "POST",
        token: authToken,
        body: {
          interactive_preview: true,
          url: normalizedLink,
          company_name: parseCompanyNameHint,
          company_website: parseCompanyWebsiteHint,
          company_link: normalizedLink,
          career_site: parseCareerSiteHint,
          industry_sector: hasExplicitOverrideLink ? "" : String(form.industry || "").trim(),
          company_size: hasExplicitOverrideLink ? "" : normalizeCompanySizeValue(form.size_range || ""),
          company_email: hasExplicitOverrideLink ? "" : String(form.email || "").trim(),
          company_phone: hasExplicitOverrideLink ? "" : String(form.phone || "").trim(),
          linkedin_url: inputSocialHints.linkedin_url || "",
          twitter_url: inputSocialHints.twitter_url || "",
          facebook_url: inputSocialHints.facebook_url || "",
          instagram_url: inputSocialHints.instagram_url || "",
          youtube_url: inputSocialHints.youtube_url || "",
          tiktok_url: inputSocialHints.tiktok_url || "",
          full_company_description: "",
        },
      });

      const requests = [companyProfileRequest];
      if (isJobLink) {
        requests.push(
          requestJsonWithFallback({
            urls: jobParseUrls,
            method: "POST",
            token: authToken,
            body: {
              url: normalizedLink,
              source: String(form.source || linkAutofill.source || "").trim(),
              company_name: parseCompanyNameHint,
              company_website: parseCompanyWebsiteHint,
            },
          })
        );
      }

      const results = await Promise.allSettled(requests);

      let nextCompanyImport = null;
      let nextJobImport = null;
      const warnings = [];

      const companyResult = results[0];
      if (companyResult?.status === "fulfilled") {
        const { response: companyRes, data: companyData } = companyResult.value;
        if (!companyRes.ok) {
          warnings.push(
            companyData?.error ||
              companyRes.statusText ||
              "Could not scrape company details from this link."
          );
        } else {
          nextCompanyImport = normalizeParsedCompanyImport({
            ...(companyData?.parsed && typeof companyData.parsed === "object"
              ? companyData.parsed
              : {}),
            contacts: Array.isArray(companyData?.contacts)
              ? companyData.contacts
              : Array.isArray(companyData?.parsed?.contacts)
                ? companyData.parsed.contacts
                : [],
          });
          if (inputSocialHints.linkedin_url && !nextCompanyImport.linkedin_url) {
            nextCompanyImport.linkedin_url = inputSocialHints.linkedin_url;
          }
          if (inputSocialHints.twitter_url && !nextCompanyImport.twitter_url) {
            nextCompanyImport.twitter_url = inputSocialHints.twitter_url;
          }
          if (inputSocialHints.facebook_url && !nextCompanyImport.facebook_url) {
            nextCompanyImport.facebook_url = inputSocialHints.facebook_url;
          }
          if (inputSocialHints.instagram_url && !nextCompanyImport.instagram_url) {
            nextCompanyImport.instagram_url = inputSocialHints.instagram_url;
          }
          if (inputSocialHints.youtube_url && !nextCompanyImport.youtube_url) {
            nextCompanyImport.youtube_url = inputSocialHints.youtube_url;
          }
          if (inputSocialHints.tiktok_url && !nextCompanyImport.tiktok_url) {
            nextCompanyImport.tiktok_url = inputSocialHints.tiktok_url;
          }
          if (normalizedLink && !nextCompanyImport.company_link && !isJobLink) {
            nextCompanyImport.company_link = normalizedLink;
          }
          setCompanyLinkImport(nextCompanyImport);
        }
      } else if (companyResult?.reason) {
        warnings.push(
          normalizeCompanySaveErrorMessage(companyResult.reason) ||
            "Could not scrape company details from this link."
        );
      }

      if (isJobLink && results[1]) {
        const jobResult = results[1];
        if (jobResult.status === "fulfilled") {
          const { response: jobRes, data: jobData } = jobResult.value;
          if (!jobRes.ok) {
            warnings.push(
              jobData?.error || jobRes.statusText || "Failed to parse job link"
            );
          } else {
            nextJobImport = normalizeParsedJobImport(jobData?.parsed || {});
          }
        } else if (jobResult.reason) {
          warnings.push(
            normalizeCompanySaveErrorMessage(jobResult.reason) || "Failed to parse job link"
          );
        }
      }

      const reconciledCompanyImport = reconcileCompanyImportWithJobImport(
        nextCompanyImport,
        nextJobImport
      );
      nextCompanyImport = reconciledCompanyImport;
      setCompanyLinkImport(nextCompanyImport);
      setJobLinkImport(nextJobImport);

      const nextFormState = buildMergedCompanyFormState(
        mergeBaseFormState,
        nextCompanyImport,
        nextJobImport,
        linkAutofill
      );
      const ensuredTeamMember = await ensureTeamMemberValue(
        nextFormState.team_member,
        authToken
      );
      const nextFormStateWithTeamMember =
        ensuredTeamMember && ensuredTeamMember !== nextFormState.team_member
          ? { ...nextFormState, team_member: ensuredTeamMember }
          : nextFormState;
      setForm(nextFormStateWithTeamMember);

      if (warnings.length) {
        setToast({
          open: true,
          type: nextCompanyImport || nextJobImport ? "warning" : "error",
          msg: warnings[0],
        });
      }

      const companyPayload = buildCompanyPayload(
        nextFormStateWithTeamMember,
        nextJobImport,
        nextCompanyImport
      );
      if (!String(companyPayload.name || "").trim()) {
        throw new Error("Could not determine the company name from this link.");
      }

      const { company: newCompany } = await saveCompanyRecord(
        companyPayload,
        authToken,
        { interactiveImport: true }
      );
      // The inline top-bar Use Link action is a review-first workflow. It
      // should close here and hand off to Activities, never continue into the
      // company profile/career preview screens.
      await finishInteractiveUseLinkImport(newCompany);
      return;
    } catch (error) {
      setToast({
        open: true,
        type: "error",
        msg:
          normalizeCompanySaveErrorMessage(error) ||
          "Could not import and create the company from this link. You can still review the fields and add it manually.",
      });
      setScreen("overview");
    } finally {
      setParsingLink(false);
    }
  };
  continueFromInlineLinkRef.current = handleContinueFromInlineLink;

  useEffect(() => {
    if (!open) {
      autoImportAttemptedRef.current = false;
      autoSubmitAttemptedRef.current = false;
      return;
    }

    if (!autoImportOnOpen || autoImportAttemptedRef.current || busy) return;
    if (!String(form.company_link || "").trim()) return;

    autoImportAttemptedRef.current = true;
    continueFromInlineLinkRef.current?.();
  }, [autoImportOnOpen, busy, form.company_link, open]);

  const validateOverview = () => {
    if (selectedExistingCompanyId) {
      setErrors({});
      return true;
    }

    const next = {};
    const missing = [];
    if (!String(form.name || "").trim()) {
      next.name = "Company name is required";
      missing.push("Company Name");
    }

    if (!String(form.website || "").trim()) {
      next.website = "Website is required";
      missing.push("Website");
    }

    const websiteOk = safeUrl(form.website);
    if (String(form.website || "").trim() && websiteOk === null) next.website = "Enter a valid website URL";

    const careerOk = safeUrl(form.career_site);
    if (String(form.career_site || "").trim() && careerOk === null) next.career_site = "Enter a valid URL";

    if (missing.length) {
      next._form = formatMissingRequiredFieldsMessage(missing);
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const validateAll = () => {
    if (selectedExistingCompanyId) {
      setErrors({});
      return true;
    }

    const next = {};
    const missing = [];

    if (!String(form.name || "").trim()) {
      next.name = "Company name is required";
      missing.push("Company Name");
    }

    if (!String(form.website || "").trim()) {
      next.website = "Website is required";
      missing.push("Website");
    }

    if (!String(form.status || "").trim()) {
      next.status = "Status is required";
      missing.push("Status");
    }

    if (!String(form.source || "").trim()) {
      next.source = "Source is required";
      missing.push("Source");
    }

    const websiteOk = safeUrl(form.website);
    if (String(form.website || "").trim() && websiteOk === null) next.website = "Enter a valid website URL";

    const linkOk = safeUrl(form.company_link);
    if (String(form.company_link || "").trim() && linkOk === null) next.company_link = "Enter a valid link URL";

    const careerOk = safeUrl(form.career_site);
    if (String(form.career_site || "").trim() && careerOk === null) next.career_site = "Enter a valid URL";

    if (missing.length) {
      next._form = formatMissingRequiredFieldsMessage(missing);
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleClose = () => {
    if (!busy) onClose?.();
  };

  const handleSubmit = async () => {
    if (!validateAll()) return;

    try {
      const authToken = token || getStoredToken();

      if (selectedExistingCompany?.companyRecord?.id) {
        if (companyLinkImport || jobLinkImport) {
          await finishInteractiveUseLinkImport(selectedExistingCompany.companyRecord);
          return;
        }
        await completeSelectionOnly(selectedExistingCompany.companyRecord);
        return;
      }

      const ensuredTeamMember = await ensureTeamMemberValue(form.team_member, authToken);
      const nextFormState =
        ensuredTeamMember && ensuredTeamMember !== form.team_member
          ? { ...form, team_member: ensuredTeamMember }
          : form;
      if (nextFormState !== form) {
        setForm(nextFormState);
      }

      const payload = buildCompanyPayload(nextFormState, jobLinkImport, companyLinkImport);
      const matchedExistingCompany =
        selectedExistingCompany?.company_id
          ? selectedExistingCompany
          : findMatchingCompanyOption(companies, {
              companyName: payload.name,
              website: payload.website || nextFormState.website,
            });

      if (matchedExistingCompany?.company_id) {
        const existingCompanyRecord =
          selectedExistingCompany?.companyRecord &&
          Number(selectedExistingCompany?.company_id || 0) ===
            Number(matchedExistingCompany.company_id || 0)
            ? selectedExistingCompany.companyRecord
            : await applyExistingCompanySelection(matchedExistingCompany);

        if (!existingCompanyRecord?.id) {
          throw new Error("Failed to load the existing company.");
        }

        if (companyLinkImport || jobLinkImport) {
          await finishInteractiveUseLinkImport(existingCompanyRecord);
          return;
        }

        if (!openProfileOnComplete) {
          await completeSelectionOnly(existingCompanyRecord);
          return;
        }

        await finalizeCompanyFlow({
          companyRecord: existingCompanyRecord,
          authToken,
          successMessage: "Company already exists",
          runCareerLeadPreview: false,
          importTarget: careerImportTarget,
        });
        return;
      }

      const { company: newCompany, alreadyExists } = await saveCompanyRecord(
        payload,
        authToken,
        { interactiveImport: Boolean(companyLinkImport || jobLinkImport) }
      );
      setCompanies((prev) => upsertCompanyOption(prev, buildCompanyOption(newCompany)));
      if (companyLinkImport || jobLinkImport) {
        await finishInteractiveUseLinkImport(newCompany);
        return;
      }
      if (!openProfileOnComplete) {
        await completeSelectionOnly(newCompany);
        return;
      }
      await finalizeCompanyFlow({
        companyRecord: newCompany,
        authToken,
        successMessage: alreadyExists ? "Company already exists" : "Company created",
        runCareerLeadPreview: Boolean(String(payload.name || newCompany?.name || "").trim()),
        importTarget: careerImportTarget,
      });
    } catch (e) {
      setToast({
        open: true,
        type: "error",
        msg: normalizeCompanySaveErrorMessage(e),
      });
    }
  };
  submitOnOpenRef.current = handleSubmit;

  useEffect(() => {
    if (!open) {
      autoSubmitAttemptedRef.current = false;
      return;
    }

    if (!autoSubmitOnOpen || autoImportOnOpen || autoSubmitAttemptedRef.current || busy) return;
    if (!String(form.name || "").trim()) return;

    autoSubmitAttemptedRef.current = true;
    submitOnOpenRef.current?.();
  }, [autoImportOnOpen, autoSubmitOnOpen, busy, form.name, open]);

  /* =========================
   * Screens
   * ========================= */
  const renderStart = () => (
    <CardShell title="Add Company" onClose={handleClose} saving={busy}>
      <Box sx={{ p: 4 }}>
        <Stack spacing={1.25}>
          <ChoiceRow
            icon={<BusinessOutlinedIcon sx={{ fontSize: 18, color: PURPLE }} />}
            title="Start from Scratch"
            subtitle="Enter the company name and we will use AI to map the company profile, find competitors, save leaders and team members as contacts, and scan for current openings after save"
            onClick={() => {
              setJobLinkImport(null);
              setCareerImportTarget(normalizedAutoImportTarget);
              setScreen("overview");
            }}
          />

          <InlineLinkEntryCard
            title="Use Link"
            subtitle="Paste a company website or job post and we will map company profile fields, find competitors, save leadership contacts, scan openings, and open the company profile"
            value={form.company_link}
            onChange={handleCompanyLinkChange}
            onSubmit={handleContinueFromInlineLink}
            placeholder="https://www.linkedin.com/jobs/view/... or https://company.com/careers/role"
            buttonText="Import & Add"
            helperText={companyLinkSummary}
            errorText={errors.company_link || ""}
            loading={parsingLink}
            disabled={busy}
          />
        </Stack>
      </Box>
    </CardShell>
  );

  const renderLink = () => (
    <CardShell title="Add Company - Link" onClose={handleClose} saving={busy}>
      <Box sx={{ p: 4 }}>
        <Stack spacing={1.25}>
          <Box>
            <Label>Paste link</Label>
            <TextField
              value={form.company_link}
              onChange={handleCompanyLinkChange}
              placeholder="https://www.linkedin.com/jobs/view/... or https://company.com/careers/role"
              fullWidth
              size="small"
              error={!!errors.company_link}
              helperText={errors.company_link || companyLinkSummary}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon sx={{ fontSize: 18, color: PURPLE }} />
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </Box>

          <Box>
            <Label>Select Source</Label>
            <TextField select value={form.source} onChange={setField("source")} fullWidth size="small" sx={fieldSx}>
              {sourceOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Stack>
      </Box>

      <FooterBar
        saving={busy}
        leftText="Back"
        onLeft={() => setScreen("start")}
        rightText="Import & Add"
        onRight={handleContinueFromInlineLink}
      />
    </CardShell>
  );

  const renderOverview = () => (
    <CardShell title="Add Company" onClose={handleClose} saving={busy}>
      <Box sx={{ px: 4, pt: 2.5, pb: 1.5 }}>
        <Section>Company Overview</Section>
      </Box>

      <Box sx={{ px: 4, pb: 4 }}>
        <Stack spacing={1.25}>
          {errors._form ? <Alert severity="error">{errors._form}</Alert> : null}

          <Box>
            <Label>Name</Label>
            <Autocomplete
              freeSolo
              openOnFocus
              selectOnFocus
              clearOnBlur={false}
              handleHomeEndKeys
              options={companies}
              loading={loadingCompanies || loadingCompanyDetails}
              value={selectedCompanyOptionValue}
              inputValue={form.name}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === "reset") return;

                const normalizedInput = normalizeCompanyName(newInputValue || "");
                if (selectedExistingCompanyId) {
                  setSelectedExistingCompany(null);
                  setCompanyLinkImport(null);
                }

                setForm((prev) => ({
                  ...prev,
                  name: normalizedInput,
                }));
                setErrors((prev) => {
                  const next = { ...prev };
                  delete next.name;
                  return next;
                });

                if (!normalizedInput) {
                  seededCompanySelectionRef.current = "";
                }
              }}
              onChange={async (_, newValue) => {
                if (typeof newValue === "string") {
                  setSelectedExistingCompany(null);
                  setCompanyLinkImport(null);
                  setForm((prev) => ({
                    ...prev,
                    name: normalizeCompanyName(newValue),
                  }));
                  return;
                }

                if (newValue?.inputValue) {
                  setSelectedExistingCompany(null);
                  setCompanyLinkImport(null);
                  setForm((prev) => ({
                    ...prev,
                    name: normalizeCompanyName(newValue.inputValue),
                  }));
                  return;
                }

                if (!newValue?.company_id) {
                  setSelectedExistingCompany(null);
                  return;
                }

                try {
                  await applyExistingCompanySelection(newValue);
                } catch (error) {
                  console.error("Failed to load selected company details:", error);
                  setToast({
                    open: true,
                    type: "error",
                    msg: error?.message || "Failed to load the selected company.",
                  });
                }
              }}
              filterOptions={filterCompanyOptions}
              getOptionLabel={getCompanyOptionLabel}
              isOptionEqualToValue={(option, value) =>
                Number(option?.company_id || 0) === Number(value?.company_id || 0)
              }
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={`${option?.company_id ?? "new"}-${option?.company_name ?? option?.inputValue ?? ""}`}
                  sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, color: "#111827", fontWeight: 700 }}>
                      {option?.inputValue
                        ? `Create "${option.inputValue}"`
                        : <CompanyOptionLabel company={option} />}
                    </Typography>
                    {!option?.inputValue && (
                      <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                        {[option?.website, option?.location].filter(Boolean).join(" | ") || "Existing company"}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  fullWidth
                  error={!!errors.name}
                  helperText={
                    errors.name ||
                    (selectedExistingCompanyId
                      ? "Existing company selected. Using this company will link it to the job without creating a duplicate."
                      : loadingCompanies
                        ? "Loading companies..."
                        : "Search an existing company or type a new one.")
                  }
                  placeholder="Amazon"
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCompanies || loadingCompanyDetails ? (
                          <CircularProgress size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>

          <Box>
            <Label>Website</Label>
            <TextField
              value={form.website}
              onChange={setField("website")}
              size="small"
              fullWidth
              error={!!errors.website}
              helperText={errors.website || ""}
              placeholder="https://www.hourglass.com"
              sx={fieldSx}
            />
          </Box>

          <Box>
            <Label>Size (Optional)</Label>
            <CompanySizeDropdownField
              value={form.size_range}
              onChange={(nextValue) =>
                setForm((prev) => ({ ...prev, size_range: nextValue }))
              }
              placeholder="50 - 200 employees"
              inputSx={fieldSx}
            />
          </Box>

          <Box>
            <Label>Career Site (Optional)</Label>
            <TextField
              value={form.career_site}
              onChange={setField("career_site")}
              size="small"
              fullWidth
              error={!!errors.career_site}
              helperText={errors.career_site || ""}
              placeholder="https://www.hourglass.com/careers"
              sx={fieldSx}
            />
          </Box>

          <Box>
            <Label>Location (city / country) (Optional)</Label>
            <LocationSearchableField
              value={form.location}
              onChange={(nextValue) =>
                setForm((prev) => ({ ...prev, location: nextValue }))
              }
              placeholder="New York, USA"
              textFieldProps={{ sx: fieldSx }}
            />
          </Box>

          <Box>
            <Label>Email (Optional)</Label>
            <TextField value={form.email} onChange={setField("email")} size="small" fullWidth sx={fieldSx} />
          </Box>

          <Box>
            <Label>Phone (Optional)</Label>
            <TextField value={form.phone} onChange={setField("phone")} size="small" fullWidth sx={fieldSx} />
          </Box>
        </Stack>
      </Box>

      <FooterBar
        saving={busy}
        leftText="Back"
        onLeft={() => setScreen("start")}
        rightText="Next"
        onRight={() => {
          if (!validateOverview()) return;
          setScreen("additional");
        }}
      />
    </CardShell>
  );

  const renderAdditional = () => (
    <CardShell title="Add Company" onClose={handleClose} saving={busy}>
      <Box sx={{ px: 4, pt: 2.5, pb: 1 }}>
        <Typography sx={{ fontSize: 11.5, color: "#6B7280", fontWeight: 700 }}>
          {summaryLine}
        </Typography>
      </Box>

      <Box sx={{ px: 4, pt: 1, pb: 1.5 }}>
        <Section>Additional Details</Section>
      </Box>

      <Box sx={{ px: 4, pb: 4 }}>
        <Stack spacing={1.25}>
          {errors._form ? <Alert severity="error">{errors._form}</Alert> : null}

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            <Box sx={{ flex: 1 }}>
              <Label>Size (Optional)</Label>
              <CompanySizeDropdownField
                value={form.size_range}
                onChange={(nextValue) =>
                  setForm((prev) => ({ ...prev, size_range: nextValue }))
                }
                placeholder="50 - 200 employees"
                inputSx={fieldSx}
              />
            </Box>

            <Box sx={{ flex: 1 }}>
              <Label>Industry (Optional)</Label>
              <TextField
                value={form.industry}
                onChange={setField("industry")}
                size="small"
                fullWidth
                placeholder="Technology"
                sx={fieldSx}
              />
            </Box>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            <Box sx={{ flex: 1 }}>
              <Label>Status</Label>
              <TextField
                select
                value={form.status}
                onChange={setField("status")}
                size="small"
                fullWidth
                error={!!errors.status}
                helperText={errors.status || ""}
                sx={fieldSx}
              >
                {COMPANY_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Label>Source</Label>
              <TextField
                select
                value={form.source}
                onChange={setField("source")}
                size="small"
                fullWidth
                error={!!errors.source}
                helperText={errors.source || ""}
                sx={fieldSx}
              >
                {sourceOptions.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Stack>

          <Box>
            <Label>Notes (optional)</Label>
            <TextField value={form.notes} onChange={setField("notes")} size="small" fullWidth multiline minRows={4} sx={fieldSx} />
          </Box>

          <Box>
            <Label>Assign Team Member (optional)</Label>
            <Autocomplete
              freeSolo
              openOnFocus
              selectOnFocus
              clearOnBlur={false}
              handleHomeEndKeys
              options={assignTeamOptions}
              loading={assignTeamLoading}
              value={selectedTeamMember}
              inputValue={form.team_member}
              onOpen={() => {
                if (!teamMembers.length && !loadingTeamMembers) fetchTeamMembersList();
              }}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === "reset") return;
                if (reason === "clear") {
                  setTeamMemberValue("");
                  return;
                }
                setTeamMemberValue(newInputValue || "");
              }}
              onChange={(_, newValue) => {
                if (typeof newValue === "string") {
                  setTeamMemberValue(newValue);
                  return;
                }
                setTeamMemberValue(newValue?.inputValue || newValue?.name || "");
              }}
              filterOptions={(options, params) => {
                const inputValue = normalizeTeamMemberName(params.inputValue).toLowerCase();
                const filtered = options.filter(
                  (option) => !inputValue || option.name.toLowerCase().includes(inputValue)
                );
                const base = dedupeAutocompleteOptions(teamMemberFilter(filtered, {
                  ...params,
                  inputValue: "",
                }));
                const exists = options.some(
                  (option) => option.name.toLowerCase() === inputValue
                );

                if (inputValue && !exists) {
                  base.push({
                    inputValue: normalizeTeamMemberName(params.inputValue),
                    id: null,
                    name: `Create "${normalizeTeamMemberName(params.inputValue)}"`,
                  });
                }

                return dedupeAutocompleteOptions(base, (option) =>
                  normalizeAutocompleteText(option?.name || option?.inputValue || "")
                );
              }}
              getOptionLabel={(option) => {
                if (typeof option === "string") return option;
                return option?.inputValue || option?.name || "";
              }}
              isOptionEqualToValue={(option, value) => {
                const optionId = option?.id ?? null;
                const valueId = value?.id ?? null;

                if (optionId != null && valueId != null) {
                  return String(optionId) === String(valueId);
                }

                return (
                  normalizeTeamMemberName(option?.name || option?.inputValue || "").toLowerCase() ===
                  normalizeTeamMemberName(value?.name || value?.inputValue || "").toLowerCase()
                );
              }}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  key={`${option?.id ?? "new"}-${option?.name ?? option?.inputValue ?? ""}`}
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Typography sx={{ fontSize: 14, color: "#111827" }}>
                    {option?.inputValue ? `Create "${option.inputValue}"` : option?.name || ""}
                  </Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  fullWidth
                  placeholder="Search or type a team member name"
                  helperText={
                    loadingTeamMembers
                      ? "Loading team members..."
                      : teamMembers.length
                        ? "Search existing team members or type a new name to create on save."
                        : teamMembersDebug || "No team members yet. Type a name to create one on save."
                  }
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {assignTeamLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        </Stack>
      </Box>

      <FooterBar
        saving={busy}
        leftText="Back"
        onLeft={() => setScreen("overview")}
        rightText={
          selectedExistingCompanyId
            ? "Use Company"
            : saving
              ? "Saving..."
              : "Add Company"
        }
        onRight={handleSubmit}
      />
    </CardShell>
  );

  const renderLeadPreview = () => (
    <CardShell
      title="Add Company - Select Openings"
      onClose={handleClose}
      saving={busy}
    >
      <Box sx={{ px: 4, pt: 2.5, pb: 1.5 }}>
        <Stack spacing={1}>
          <Section>Career Site Openings</Section>
          <Typography sx={{ fontSize: 12.5, color: "#374151" }}>
            {`${createdCompanyForLeadFlow?.name || form.name || "Company"} has ${
              careerLeadPreviewRows.length
            } opening${careerLeadPreviewRows.length === 1 ? "" : "s"} ready to add as ${
              careerImportTarget === "jobs" ? "jobs" : "leads"
            }.`}
          </Typography>
          {careerLeadPreviewMeta.previewSource === "google_jobs_fallback" ? (
            <Stack spacing={0.5}>
              <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
                {`Used ${careerLeadPreviewMeta.sourceLabel || "Google Jobs"} fallback results because an AI careers-page search was unavailable or no scannable openings were found.`}
              </Typography>
              {!!careerLeadPreviewMeta.bestSourceUrl && (
                <Box
                  component="a"
                  href={careerLeadPreviewMeta.bestSourceUrl}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    color: PURPLE,
                    fontSize: 12,
                    fontWeight: 800,
                    textDecoration: "none",
                    width: "fit-content",
                  }}
                >
                  Open best matching public job result
                </Box>
              )}
            </Stack>
          ) : !!careerLeadPreviewMeta.careerSite && (
            <Typography sx={{ fontSize: 11.5, color: "#6B7280" }}>
              {`Scanned ${careerLeadPreviewMeta.scannedPages || 0} page${
                careerLeadPreviewMeta.scannedPages === 1 ? "" : "s"
              } and reviewed ${careerLeadPreviewMeta.pagesConsidered || 0} career page${
                careerLeadPreviewMeta.pagesConsidered === 1 ? "" : "s"
              } from ${careerLeadPreviewMeta.careerSite}.`}
            </Typography>
          )}
          {careerLeadPreviewWarnings.length > 0 && (
            <Alert severity="warning" sx={{ alignItems: "flex-start" }}>
              <Box>
                {careerLeadPreviewWarnings.slice(0, 3).map((warning, index) => (
                  <Typography key={`${warning}-${index}`} sx={{ fontSize: 12.5 }}>
                    {warning}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}
        </Stack>
      </Box>

      <Box sx={{ px: 4, pb: 4 }}>
        <GenericDataTable
          columns={careerLeadPreviewColumns}
          rows={careerLeadPreviewRows}
          defaultOrderBy="job_title"
          includeSelectionColumn={true}
          selectedIds={selectedCareerLeadIds}
          onSelectedIdsChange={setSelectedCareerLeadIds}
          initialRowsPerPage={10}
          tableContainerSx={{
            border: `1px solid ${SOFT_BORDER}`,
            borderRadius: 2.5,
            overflowY: "auto",
            maxHeight: "52vh",
          }}
          paginationBarSx={{ pb: 0 }}
          renderRow={(row) => (
            <>
              <TableCell sx={{ minWidth: 260 }}>
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: "#111827" }}>
                  {row.job_title || "-"}
                </Typography>
              </TableCell>
              <TableCell sx={{ minWidth: 170 }}>
                <Typography sx={{ fontSize: 12.5, color: "#374151" }}>
                  {row.location || "-"}
                </Typography>
              </TableCell>
              <TableCell sx={{ minWidth: 120 }}>
                <Typography sx={{ fontSize: 12.5, color: "#374151" }}>
                  {row.employment_type || "-"}
                </Typography>
              </TableCell>
              <TableCell sx={{ minWidth: 100 }}>
                <Typography sx={{ fontSize: 12.5, color: "#374151", textTransform: "capitalize" }}>
                  {row.confidence || "-"}
                </Typography>
              </TableCell>
              <TableCell sx={{ minWidth: 120 }}>
                {row.job_url ? (
                  <Box
                    component="a"
                    href={row.job_url}
                    target="_blank"
                    rel="noreferrer"
                    sx={{
                      color: PURPLE,
                      fontSize: 12.5,
                      fontWeight: 800,
                      textDecoration: "none",
                    }}
                  >
                    Open link
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 12.5, color: "#9CA3AF" }}>-</Typography>
                )}
              </TableCell>
            </>
          )}
        />
      </Box>

      <FooterBar
        saving={busy}
        leftText="Skip for now"
        onLeft={handleSkipCareerLeads}
        rightText={
          importingCareerLeads
            ? `Adding ${careerImportTarget === "jobs" ? "jobs" : "leads"}...`
            : `Add Selected ${careerImportTarget === "jobs" ? "Jobs" : "Leads"} (${selectedCareerLeadIds.length})`
        }
        onRight={handleImportCareerLeads}
      />
    </CardShell>
  );

  const current = () => {
    if (screen === "start") return renderStart();
    if (screen === "link") return renderLink();
    if (screen === "overview") return renderOverview();
    if (screen === "additional") return renderAdditional();
    if (screen === "leadPreview") return renderLeadPreview();
    return renderStart();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth={screen === "leadPreview" ? "xl" : "sm"}
        fullWidth
        PaperProps={{
          sx: { borderRadius: 4, bgcolor: "transparent", boxShadow: "none" },
        }}
      >
        <Box sx={{ p: 1, bgcolor: "transparent" }}>{current()}</Box>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3200}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
      >
        <Alert severity={toast.type} onClose={() => setToast((p) => ({ ...p, open: false }))}>
          {toast.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
